import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, Sparkles, ArrowRight, Zap, Calculator, 
  Briefcase, Satellite, Bot, CheckCircle2, RefreshCw, X, MessageSquare, AlertCircle, Send, Check,
  BookOpen, ChevronDown, Layers, ShieldCheck, FileCheck, Scale,
  Paperclip, UploadCloud, FileText, Trash2, Building2, ExternalLink, Globe, Clock
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { Language } from '../utils/i18n';

interface SmartAgentDispatcherProps {
  onLaunchAgent: (agentId: string, preferredLang?: any, hubContext?: string, dynamicAgentData?: any) => void;
  language: Language;
  currentUserEmail?: string;
  enterpriseName?: string;
  hasEnterprise?: boolean;
  onOpenEnterpriseDashboard?: () => void;
}

interface RecommendedAgent {
  id: string;
  nameUk: string;
  nameEn: string;
  namePl?: string;
  nameFr?: string;
  nameDe?: string;
  nameIt?: string;
  nameEs?: string;
  namePt?: string;
  nameSv?: string;
  descriptionUk: string;
  descriptionEn: string;
  descriptionPl?: string;
  descriptionFr?: string;
  descriptionDe?: string;
  descriptionIt?: string;
  descriptionEs?: string;
  descriptionPt?: string;
  descriptionSv?: string;
  icon: string;
  categoryUk: string;
  categoryEn?: string;
  categoryPl?: string;
  categoryFr?: string;
  categoryDe?: string;
  categoryIt?: string;
  categoryEs?: string;
  categoryPt?: string;
  categorySv?: string;
  matchReasonUk: string;
  matchReasonEn: string;
  matchReasonPl: string;
  matchReasonFr?: string;
  matchReasonDe?: string;
  matchReasonIt?: string;
  matchReasonEs?: string;
  matchReasonPt?: string;
  matchReasonSv?: string;
  score: number;
}

const QUICK_PROMPTS_MAP: Record<Language, { label: string; query: string }[]> = {
  uk: [
    { label: "🚀 Аудит сайту & Конверсії (CRO)", query: "аудит сайту UI/UX ціннісна пропозиція конверсія CRO лендинг залучення та утримання клієнтів" },
    { label: "📄 Скласти договір за ДСТУ 4163", query: "аудит договору оренди приміщення за ДСТУ 4163:2020 комерційна нерухомість" },
    { label: "🏗️ Кошторис РЕКН/КНУ за 3 хв", query: "розрахунок локального кошторису ремонтні роботи РЕКН КНУ дефектний акт Excel" },
    { label: "🏛️ Податкова декларація ПДВ/СМКОР", query: "заблокована податкова накладна СМКОР ПДВ зобов'язання ДПС ПКУ ст 187" },
    { label: "💊 Перевірка сумісності ліків (ДЛУ)", query: "перевірка сумісності ліків міжлікарські взаємодії довідник ДЛУ" },
    { label: "⚡ Розрахунок СЕС & Net Billing", query: "розрахунок окупності СЕС 250 кВт акумулятори BESS та Net Billing" },
    { label: "🛰️ Моніторинг полів Sentinel/Maxar", query: "супутниковий моніторинг NDVI полів Sentinel-2 Maxar 0.3m ДАР" }
  ],
  en: [
    { label: "🚀 Website CRO & UI/UX Audit", query: "website conversion rate optimization CRO UI UX landing page value proposition retention audit" },
    { label: "🇪🇺 CBAM Carbon Border & Emissions ($CO_2$)", query: "EU CBAM carbon border adjustment embedded emissions direct indirect calculation CO2 registry" },
    { label: "🏗️ FIDIC IPC & Contract Administration", query: "FIDIC Red Yellow Book Interim Payment Certificate IPC claims clause 20 Extension of Time" },
    { label: "📄 Commercial Contract Audit & NDA", query: "commercial lease contract audit trap clauses liability and compliance" },
    { label: "🏛️ Corporate Tax & Cross-Border VAT", query: "tax declaration review VAT compliance invoices and reporting" },
    { label: "⚡ Solar PV & Microgrid ROI", query: "solar farm ROI calculation 250 kW BESS battery net billing" },
    { label: "🛰️ Satellite Field Monitoring", query: "satellite NDVI field monitoring Sentinel-2 Maxar 0.3m agriculture" }
  ],
  pl: [
    { label: "🚀 Audyt Strony WWW & Konwersji (CRO)", query: "audyt strony internetowej landing page UX UI optymalizacja konwersji CRO retencja" },
    { label: "🏛️ KSeF FA_VAT 2 & JPK_V7 (Audytor)", query: "weryfikacja faktury KSeF FA(2) deklaracja JPK_V7 Biała Lista podatników VAT" },
    { label: "📄 Umowa B2B & Spółka z o.o. (KSH)", query: "audyt umowy najmu lokalu handlowego i umowy B2B klauzule niedozwolone RODO" },
    { label: "🏗️ Kosztorys budowlany KNR / SEKOCENBUD", query: "kosztorys budowlany prace remontowe KNR przedmiar Excel SEKOCENBUD" },
    { label: "🇪🇺 ZUS A1 & Delegowanie do UE", query: "zaświadczenie ZUS A1 delegowanie pracowników do UE rozporządzenie 883/2004" },
    { label: "⚡ Kalkulacja farmy PV / OZE", query: "kalkulacja opłacalności fotowoltaika PV 250 kW magazyn energii net-billing" },
    { label: "🛰️ Satelitarny monitoring pól Sentinel", query: "satelitarny monitoring NDVI upraw rolnych Sentinel-2 Copernicus ARiMR" }
  ],
  fr: [
    { label: "📄 Audit de Contrat Commercial", query: "audit contrat bail commercial clauses abusives loi pinel code de commerce" },
    { label: "🏗️ Devis BTP & RE2020 / CCAG", query: "chiffrage devis travaux BTP CCAG travaux CCTP RE2020 export excel" },
    { label: "🏛️ E-Facturation Factur-X / TVA", query: "audit e-facture Factur-X Chorus Pro TVA CGI déduction fiscale" },
    { label: "💊 Interactions Médicaments RCP", query: "interactions médicamenteuses contre-indications ANSM RCP" },
    { label: "⚡ Rentabilité Solaire PV / BESS", query: "calcul rentabilité ferme solaire PV 250 kW batterie stockage autoconsommation" },
    { label: "🛰️ Télédétection Parcelles Sentinel", query: "télédétection NDVI cultures agricoles Sentinel-2 RPG PAC ADEME" }
  ],
  de: [
    { label: "📄 Prüfung von Wirtschaftsverträgen", query: "vertragsprüfung gewerberaummietvertrag AGB-klauseln BGB HGB haftung" },
    { label: "🏗️ Baukostenermittlung VOB/B & DIN 276", query: "kostenberechnung VOB/B DIN 276 HOAI honorar baupreise excel export" },
    { label: "🏛️ E-Rechnung XRechnung & ZUGFeRD", query: "prüfung e-rechnung XRechnung ZUGFeRD 2.2 § 14 UStG GoBD Peppol" },
    { label: "💊 Arzneimittel-Interaktionsprüfung", query: "arzneimittelinteraktionen kontraindikationen fachinformation BfArM" },
    { label: "⚡ Wirtschaftlichkeit PV & BESS", query: "wirtschaftlichkeitsberechnung PV-anlage 250 kW batteriespeicher net-billing" },
    { label: "🛰️ Satellitäres Feldmonitoring Sentinel", query: "satellitenüberwachung NDVI ackerflächen Sentinel-2 Copernicus landwirtschaft" }
  ],
  it: [
    { label: "📄 Audit Contratti Commerciali", query: "audit contratto locazione commerciale clausole vessatorie codice civile inadempimento" },
    { label: "🏗️ Computo Metrico Estimativo", query: "computo metrico estimativo prezziario regionale SAL capitolato speciale appalto excel" },
    { label: "🏛️ FatturaPA & Modello F24", query: "fattura elettronica SDI FatturaPA split payment PA modello F24 IVA agenzia entrate" },
    { label: "💊 Interazioni Farmaci (AIFA)", query: "interazioni tra farmaci controindicazioni prontuario terapeutico AIFA" },
    { label: "⚡ Fotovoltaico & Comunità CER", query: "calcolo rendimento impianto fotovoltaico 250 kW accumulo BESS comunità energetiche" },
    { label: "🛰️ Monitoraggio Satellitare Sentinel", query: "monitoraggio satellitare NDVI colture agricole Sentinel-2 AGEA fascicolo aziendale" }
  ],
  es: [
    { label: "📄 Auditoría de Contratos B2B", query: "auditoría contrato arrendamiento local comercial código civil cláusulas abusivas" },
    { label: "🏗️ Mediciones y Presupuestos BTP", query: "mediciones y presupuestos obra cuadro de precios descompuestos CTE excel" },
    { label: "🏛️ FacturaE & Modelo 303 / 111", query: "factura electrónica FacturaE VeriFactu modelo 303 IVA retenciones IRPF AEAT" },
    { label: "💊 Interacciones Medicamentos (CIMA)", query: "interacciones medicamentosas contraindicaciones prospecto CIMA AEMPS" },
    { label: "⚡ Rentabilidad Solar & Autoconsumo", query: "cálculo rentabilidad parque solar 250 kW almacenamiento baterías autoconsumo" },
    { label: "🛰️ Teledetección Agrícola Sentinel", query: "teledetección agrícola NDVI parcelas Sentinel-2 SIGPAC PAC FEGA" }
  ],
  pt: [
    { label: "📄 Auditoria de Contratos B2B", query: "auditoria contrato arrendamento comercial código civil cláusulas abusivas" },
    { label: "🏗️ Medições e Orçamentos de Obra", query: "orçamento de obras mapa de quantidades medições caderno encargos excel" },
    { label: "🏛️ Faturação SAF-T & IVA AT", query: "faturação eletrónica SAF-T PT código ATCUD declaração periódica IVA Autoridade Tributária" },
    { label: "💊 Interações Medicamentosas", query: "interações medicamentosas contraindicações prontuário terapêutico INFARMED" },
    { label: "⚡ Energia Solar & Autoconsumo UPAC", query: "estudo viabilidade solar fotovoltaico 250 kW armazenamento baterias UPAC" },
    { label: "🛰️ Deteção Remota Agrícola Sentinel", query: "monitorização satélite NDVI parcelas agrícolas Sentinel-2 IFAP" }
  ],
  sv: [
    { label: "📄 Avtalsgranskning & Kommersiell Rätt", query: "granskning kommersiellt hyresavtal avtalsklausuler skadestånd köplagen" },
    { label: "🏗️ Byggkalkyl & ÄTA-arbete (AB 04)", query: "byggkalkyl mängdförteckning ÄTA-arbete AB 04 ABT 06 AMA excel" },
    { label: "🏛️ e-Faktura PEPPOL & Momsdeklaration", query: "e-faktura PEPPOL BIS 3.0 Svefaktura momsdeklaration F-skatt Skatteverket" },
    { label: "💊 Läkemedelsinteraktioner (FASS)", query: "läkemedelsinteraktioner kontraindikationer bipacksedel FASS Läkemedelsverket" },
    { label: "⚡ Solcellsanläggning & Batterilager", query: "lönsamhetskalkyl solceller 250 kW batterilagring stödtjänster Svenska kraftnät" },
    { label: "🛰️ Satellitövervakning av Åkermark", query: "satellitbaserad NDVI-övervakning åkermark Sentinel-2 Jordbruksverket SAM" }
  ]
};

const NORMATIVE_PRESETS_MAP: Record<Language, { category: string; items: { code: string; query: string }[] }[]> = {
  uk: [
    {
      category: "🏛️ ПКУ / ДПС (Податковий кодекс)",
      items: [
        { code: "ПКУ ст. 187 (ПДВ & СМКОР)", query: "аудит ПДВ зобов'язань розблокування податкових накладних СМКОР ДПС" },
        { code: "ПКУ ст. 291 (ФОП 3 група)", query: "звітність ФОП єдиний податок 3 група розрахунок ліміту доходу ДПС" },
        { code: "ПКУ ст. 141 (ПнВК Дія Сіті)", query: "податок на виведений капітал ПнВК Дія Сіті пільгова ставка" },
        { code: "ПКУ ст. 268 (Туразбір людо-діб)", query: "туристичний збір розрахунок людо-діб ставки для готелів ДПС" },
        { code: "ПКУ ст. 212 (Акциз СЕАРП)", query: "акцизний податок пальне СЕАРП акцизні склади ДПС" }
      ]
    },
    {
      category: "📜 ДСТУ 4163:2020 / ПКМУ / СЕВ ЗЕС",
      items: [
        { code: "ДСТУ 4163:2020 (Реквізити)", query: "скласти договір за ДСТУ 4163:2020 з правильними реквізитами та підписами" },
        { code: "🇪🇺 Реєстри ЄС (VIES / KRS / NIP)", query: "перевірка контрагента в реєстрах ЄС Польща VIES KRS Biała Lista NIP перевірка права підпису та Split Payment" },
        { code: "ПКМУ № 55 (Діловодство)", query: "перевірка відповідності інструкції з діловодства ПКМУ 55" },
        { code: "УКРНОІВІ / СЕВ ЗЕС (ТМ/ПЗ)", query: "реєстрація торговельної марки ТМ комп'ютерної програми СЕВ ЗЕС" },
        { code: "GDPR / Захист даних", query: "аудит відповідності GDPR та захисту персональних даних Політика конфіденційності" }
      ]
    },
    {
      category: "🏗️ ДБН / РЕКН / КНУ (Будівництво)",
      items: [
        { code: "РЕКН / КНУ 2021 (Кошторис)", query: "розрахунок локального кошторису ремонт дефектний акт РЕКН КНУ Excel" },
        { code: "ДБН А.3.1-5:2016 (Будмайданчик)", query: "аудит виконавчої документації будмайданчика ДБН А.3.1-5:2016" },
        { code: "Акти КБ-2в / КБ-3", query: "перевірка актів приймання виконаних будівельних робіт КБ-2в та КБ-3" }
      ]
    },
    {
      category: "🛰️ ДЗЗ / Sentinel / Maxar / ДАР",
      items: [
        { code: "Sentinel-2 (NDVI / NBR 10m)", query: "супутниковий моніторинг NDVI полів Sentinel-2 точне землеробство" },
        { code: "Maxar 0.3m / Planet 3m", query: "викачати супутниковий знімок Maxar 0.3m PlanetScope оцінка" },
        { code: "ДАР / Проби NPK / Паї ДЗК", query: "субсидії ДАР аналіз ґрунтових проб NPK аудит оренди паїв ДЗК" }
      ]
    }
  ],
  en: [
    {
      category: "🏛️ Tax & Revenue (VAT / CIT / Audits)",
      items: [
        { code: "VAT & Corporate Tax Review", query: "VAT compliance audit corporate income tax declaration reporting" },
        { code: "SME / Sole Proprietorship Tax", query: "sole proprietor tax reporting income limit calculation and compliance" },
        { code: "IP / Tech Tax Regimes", query: "tech company intellectual property preferential tax regime audit" },
        { code: "Excise & Customs Regulation", query: "excise duties customs clearance compliance audit" }
      ]
    },
    {
      category: "📜 Commercial Law & GDPR / ISO",
      items: [
        { code: "Contract Drafting & Review", query: "draft commercial contract standard clauses liabilities and terms" },
        { code: "🇪🇺 EU Registers (VIES / KRS / VAT)", query: "verify counterparty in EU registers Poland VIES KRS White List VAT NIP signing authority due diligence" },
        { code: "GDPR & Privacy Compliance", query: "GDPR audit data privacy policy compliance terms of service" },
        { code: "Trademark & IP Protection", query: "trademark registration patent software copyright filing" }
      ]
    },
    {
      category: "🏗️ Construction & Cost Estimation",
      items: [
        { code: "Bill of Quantities (BOQ)", query: "bill of quantities cost calculation construction repair works excel" },
        { code: "Site Safety & Quality Control", query: "construction site safety inspection technical compliance audit" },
        { code: "Acceptance Acts & Valuation", query: "construction work acceptance acts milestone valuation audit" }
      ]
    },
    {
      category: "🛰️ Earth Observation & Satellite (GEOINT)",
      items: [
        { code: "Sentinel-2 (NDVI / Multi-band)", query: "satellite vegetation monitoring NDVI Sentinel-2 precision agri" },
        { code: "Very High-Res (Maxar 0.3m)", query: "high resolution satellite imagery Maxar Planet damage evaluation" },
        { code: "Land Cadastre & Soil Analytics", query: "cadastral boundary mapping soil NPK samples agricultural subsidy" }
      ]
    }
  ],
  pl: [
    {
      category: "🏛️ Prawo Podatkowe / KSeF / VAT / CIT",
      items: [
        { code: "KSeF & Podatek VAT (JPK_V7)", query: "weryfikacja faktury KSeF plik JPK_V7 deklaracja VAT i rozliczenie" },
        { code: "🇪🇺 Biała Lista & KRS (Weryfikacja)", query: "sprawdzenie kontrahenta w rejestrach VIES Biała Lista MF KRS NIP reprezentacja zarządu rachunek bankowy" },
        { code: "Ryczałt & JDG (Jednoosobowa DG)", query: "rozliczenie JDG ryczałt od przychodów ewidencjonowanych limity podatkowe" },
        { code: "CIT Estoński / Ulga B+R", query: "estoński CIT ulga na badania i rozwój B+R IP Box wdrożenie" }
      ]
    },
    {
      category: "📜 Kodeks Cywilny / RODO / ISO / ZUS",
      items: [
        { code: "Kodeks cywilny (Umowy B2B)", query: "audyt umowy handlowej B2B kary umowne klauzule niedozwolone" },
        { code: "RODO & Ochrona Danych", query: "audyt zgodności z RODO polityka prywatności ochrona danych osobowych" },
        { code: "Urząd Patentowy RP (Znaki TM)", query: "rejestracja znaku towarowego w UPRP ochrona praw autorskich software" }
      ]
    },
    {
      category: "🏗️ Budownictwo & Kosztorysowanie (KNR)",
      items: [
        { code: "KNR & Sekocenbud (Kosztorys)", query: "sporządzenie kosztorysu budowlanego stawki KNR przedmiar robót Excel" },
        { code: "Prawo Budowlane (Nadzór & BHP)", query: "dziennik budowy odbiór techniczny zgodność z Prawem Budowlanym" },
        { code: "Protokoły Odbioru Robót", query: "weryfikacja protokołu odbioru robót budowlanych rozliczenie inwestycji" }
      ]
    },
    {
      category: "🛰️ Teledetekcja & Satelity (Copernicus / ARiMR)",
      items: [
        { code: "Sentinel-2 (NDVI / Wilgotność)", query: "satelitarny monitoring upraw wskaźnik NDVI wilgotność gleby Copernicus" },
        { code: "Ortofotomapa Maxar / Geoportal", query: "pobranie zdjęcia satelitarnego wysokiej rozdzielczości Maxar Geoportal" },
        { code: "Dopłaty ARiMR & Działki EGiB", query: "dopłaty bezpośrednie ARiMR audyt granic działek ewidencyjnych EGiB" }
      ]
    }
  ],
  fr: [
    {
      category: "🏛️ Fiscalité / Factur-X / TVA / CGI",
      items: [
        { code: "Factur-X & Chorus Pro (CGI)", query: "audit e-facture Factur-X Chorus Pro TVA CGI déduction fiscale" },
        { code: "Impôt sur les Sociétés (IS / CIR)", query: "déclaration impôt sur les sociétés IS crédits impôt recherche CIR" },
        { code: "Micro-Entreprise & URSSAF", query: "déclaration micro-entrepreneur URSSAF cotisations sociales plafonds" }
      ]
    },
    {
      category: "📜 Code de Commerce / Travail / RGPD",
      items: [
        { code: "Baux Commerciaux 3-6-9 (Pinel)", query: "audit bail commercial 3-6-9 clause résolutoire révision triennale ILC" },
        { code: "Statuts SAS / SARL (INPI)", query: "rédaction statuts SAS SARL formalités INPI Guichet Unique" },
        { code: "CNIL & RGPD France", query: "audit conformité RGPD CNIL registre traitements registre art 30" }
      ]
    },
    {
      category: "🏗️ BTP / RE2020 / CCAG Travaux",
      items: [
        { code: "CCAG Travaux 2021 (Marchés)", query: "analyse CCAG travaux 2021 pénalités retard décompte général définitif" },
        { code: "Réglementation RE2020 BTP", query: "conformité environnementale RE2020 étude thermique bilan carbone" }
      ]
    },
    {
      category: "🛰️ Télédétection & Cadastre (RPG / PAC)",
      items: [
        { code: "Sentinel-2 (NDVI / Cultures)", query: "suivi satellitaire cultures NDVI Sentinel-2 surveillance parcellaire" },
        { code: "Registre Parcellaire RPG / PAC", query: "audit déclarations PAC aides RPG découpage cadastral" }
      ]
    }
  ],
  de: [
    {
      category: "🏛️ Deutsches Steuerrecht / § 14 UStG / GoBD",
      items: [
        { code: "XRechnung & ZUGFeRD 2.2", query: "prüfung e-rechnung XRechnung ZUGFeRD 2.2 § 14 UStG GoBD Peppol" },
        { code: "USt-IdNr & Leitweg-ID Audit", query: "umsatzsteuer prüfung USt-IdNr Leitweg-ID § 14 UStG Vorsteuerabzug" },
        { code: "Gewerbesteuer & KSt Erklärungen", query: "gewerbesteuererklärung körperschaftsteuer rückstellungen handelsbilanz" }
      ]
    },
    {
      category: "📜 Gesellschafts- & Arbeitsrecht (BGB / HGB)",
      items: [
        { code: "GmbH & UG Satzung (GmbHG)", query: "gesellschaftsvertrag GmbH UG haftungsbeschränkt Handelsregister HRB" },
        { code: "Arbeitsrecht & NachwG / MiLoG", query: "arbeitsvertrag NachwG mindestlohn MiLoG probezeit überstunden" },
        { code: "DSGVO & BDSG Compliance", query: "datenschutz audit DSGVO BDSG verzeichnis art 30 DSFA datenschutzbeauftragter" }
      ]
    },
    {
      category: "🏗️ Bauvertragsrecht VOB/B & HOAI 2021",
      items: [
        { code: "VOB/B Bauvertrag & AGB-Prüfung", query: "prüfung bauvertrag VOB/B abnahme § 12 vertragsstrafe mängelhaftung" },
        { code: "HOAI 2021 & DIN 276 Kosten", query: "honorarberechnung HOAI 2021 leistungsphasen DIN 276 kostengruppen" }
      ]
    },
    {
      category: "🛰️ Copernicus & Geoportal (GEOINT / LkSG)",
      items: [
        { code: "Sentinel-2 (NDVI / Bodenfeuchte)", query: "satellitenüberwachung NDVI ackerflächen Sentinel-2 Copernicus landwirtschaft" },
        { code: "Lieferkettengesetz LkSG (§ 5)", query: "LkSG lieferkettensorgfaltspflichtengesetz risikoanalyse BAFA-bericht" }
      ]
    }
  ],
  it: [
    {
      category: "🏛️ Fisco & IVA (Agenzia delle Entrate)",
      items: [
        { code: "FatturaPA & SDI (FPR12)", query: "fattura elettronica SDI FatturaPA split payment PA modello F24 agenzia entrate" },
        { code: "Modello F24 (Codici Tributo)", query: "calcolo versamento F24 codice tributo erario inps ravvedimento operoso" },
        { code: "Regime Forfettario 15% / 5%", query: "regime forfettario partita iva limite 85000 coefficiente di redditività" }
      ]
    },
    {
      category: "📜 Diritto Commerciale (Codice Civile / GDPR)",
      items: [
        { code: "Contratti B2B & Clausole", query: "audit contratti locazione commerciale clausole vessatorie codice civile" },
        { code: "Società S.r.l. & Statuti (CCIAA)", query: "statuto srl srls registro imprese cciaa delibere assemblea" },
        { code: "Privacy & GDPR Garante", query: "audit gdpr garante privacy registro dei trattamenti informativa privacy" }
      ]
    },
    {
      category: "🏗️ Edilizia & Appalti (Codice Appalti)",
      items: [
        { code: "Computo Metrico Estimativo", query: "computo metrico estimativo prezziario regionale SAL contabilità lavori" },
        { code: "Capitolato Speciale Appalto", query: "capitolato speciale appalto codice contratti pubblici d.lgs 36" }
      ]
    },
    {
      category: "🛰️ Agricoltura & Dati Satellitari (AGEA)",
      items: [
        { code: "Sentinel-2 (NDVI / AGEA)", query: "monitoraggio satellitare NDVI colture agricole Sentinel-2 AGEA fascicolo aziendale" }
      ]
    }
  ],
  es: [
    {
      category: "🏛️ AEAT / Fiscalidad (IVA / IRPF)",
      items: [
        { code: "FacturaE & Veri*Factu (Crea y Crece)", query: "factura electrónica FacturaE VeriFactu modelo 303 IVA retenciones IRPF AEAT" },
        { code: "Modelo 303 & Modelo 111", query: "declaración trimestral IVA modelo 303 retenciones profesionales modelo 111" },
        { code: "Autónomos & Impuesto Sociedades", query: "alta autónomos IAE estimación directa simplificada impuesto sociedades IS" }
      ]
    },
    {
      category: "📜 Mercantil & Contratos (Código de Comercio)",
      items: [
        { code: "Contratos B2B & Mercantil", query: "auditoría contratos mercantiles arrendamiento local cláusulas penales" },
        { code: "Sociedades S.L. & Estatutos (BORME)", query: "estatutos sociedad limitada SL constitución registro mercantil BORME" },
        { code: "RGPD & AEPD Compliance", query: "auditoría RGPD AEPD registro de actividades de tratamiento política privacidad" }
      ]
    },
    {
      category: "🏗️ Construcción & CTE (Código Técnico)",
      items: [
        { code: "Presupuestos Precios Descompuestos", query: "mediciones y presupuestos obra cuadro de precios descompuestos CTE excel" },
        { code: "Ley de Contratos Sector Público", query: "licitaciones públicas LCSP pliego de cláusulas administrativas DEUC" }
      ]
    },
    {
      category: "🛰️ Teledetección Agrícola (SIGPAC / FEGA)",
      items: [
        { code: "Sentinel-2 (NDVI / SIGPAC)", query: "teledetección agrícola NDVI parcelas Sentinel-2 SIGPAC PAC FEGA" }
      ]
    }
  ],
  pt: [
    {
      category: "🏛️ Autoridade Tributária (SAF-T / IVA)",
      items: [
        { code: "Faturação SAF-T & ATCUD (AT)", query: "faturação eletrónica SAF-T PT código ATCUD declaração periódica IVA Autoridade Tributária" },
        { code: "Declaração Periódica do IVA", query: "declaração periódica IVA apuramento imposto retenção na fonte IRS IRC" },
        { code: "Trabalhador Independente & Recibos Verdes", query: "recibos verdes trabalhadores independentes segurança social regime simplificado" }
      ]
    },
    {
      category: "📜 Direito Comercial & RGPD",
      items: [
        { code: "Contratos Comerciais (Código Civil)", query: "auditoria contratos comerciais arrendamento cláusulas penais código comercial" },
        { code: "Sociedades por Quotas Lda (IRN)", query: "pacto social constituição sociedade por quotas Lda registo comercial IRN" },
        { code: "RGPD & CNPD Compliance", query: "auditoria RGPD CNPD registo de atividades de tratamento encarregado proteção dados" }
      ]
    },
    {
      category: "🏗️ Construção & Obras Públicas (CCP)",
      items: [
        { code: "Mapa de Quantidades & Orçamentos", query: "orçamento de obras mapa de quantidades medições caderno encargos excel" },
        { code: "Código dos Contratos Públicos (CCP)", query: "concursos públicos código dos contratos públicos CCP auto de medição" }
      ]
    },
    {
      category: "🛰️ Deteção Remota Agrícola (IFAP / PAC)",
      items: [
        { code: "Sentinel-2 (NDVI / IFAP)", query: "monitorização satélite NDVI parcelas agrícolas Sentinel-2 IFAP pedido único" }
      ]
    }
  ],
  sv: [
    {
      category: "🏛️ Skatteverket (Moms / F-skatt)",
      items: [
        { code: "PEPPOL BIS 3.0 & Momsdeklaration", query: "e-faktura PEPPOL BIS 3.0 Svefaktura momsdeklaration F-skatt Skatteverket" },
        { code: "Moms 25% & Omvänd Byggmoms", query: "momsredovisning 25% omvänd skattskyldighet inom byggsektorn skattekonto" },
        { code: "Enskild Firma & Aktiebolag AB", query: "enskild firma inkomstdeklaration bolagsskatt aktiebolag periodiseringsfond" }
      ]
    },
    {
      category: "📜 Avtalsrätt & GDPR (Bolagsverket)",
      items: [
        { code: "Kommersiella Avtal (Avtalslagen)", query: "granskning kommersiellt hyresavtal avtalsklausuler skadestånd köplagen" },
        { code: "Aktiebolag AB Bolagsordning", query: "bolagsordning stiftelseurkund nybildning aktiebolag Bolagsverket" },
        { code: "GDPR & IMY Integritetsskydd", query: "gdpr granskning register över behandlingsaktiviteter integritetsskyddsmyndigheten IMY" }
      ]
    },
    {
      category: "🏗️ Bygg & Entreprenad (AB 04 / ABT 06)",
      items: [
        { code: "Byggkalkyl & ÄTA-arbete (AB 04)", query: "byggkalkyl mängdförteckning ÄTA-arbete AB 04 ABT 06 AMA excel" },
        { code: "Offentlig Upphandling LOU", query: "offentlig upphandling lagen om offentlig upphandling LOU anbud ESPD" }
      ]
    },
    {
      category: "🛰️ Satellitövervakning (Jordbruksverket)",
      items: [
        { code: "Sentinel-2 (NDVI / SAM-ansökan)", query: "satellitbaserad NDVI-övervakning åkermark Sentinel-2 Jordbruksverket SAM" }
      ]
    }
  ]
};

export type ProfessionalRole = 'all' | 'accounting' | 'construction' | 'legal' | 'poland' | 'us' | 'agri';

interface RoleTabConfig {
  id: ProfessionalRole;
  iconName: string;
  labelUk: string;
  labelEn: string;
  labelPl: string;
  labelDe: string;
  labelFr: string;
  flagshipIds: string[];
}

const ROLE_TABS: RoleTabConfig[] = [
  {
    id: 'all',
    iconName: 'Layers',
    labelUk: '🌐 Усі сфери',
    labelEn: '🌐 All Domains',
    labelPl: '🌐 Wszystkie branże',
    labelDe: '🌐 Alle Bereiche',
    labelFr: '🌐 Tous Domaines',
    flagshipIds: ['contract-auditor', 'dstu-pkmu-auditor', 'construction-acceptance-auditor', 'ksef-vat-auditor']
  },
  {
    id: 'accounting',
    iconName: 'Calculator',
    labelUk: '💼 Бухгалтерія & Податки',
    labelEn: '💼 Accounting & Taxes',
    labelPl: '💼 Księgowość & Podatki',
    labelDe: '💼 Buchhaltung & Steuern',
    labelFr: '💼 Comptabilité & Fiscalité',
    flagshipIds: ['tax-financial-analyst', 'tax-pn-unblocker-auditor', 'public-accountant-kekv']
  },
  {
    id: 'construction',
    iconName: 'Briefcase',
    labelUk: '🏗️ Будівництво & Кошторис',
    labelEn: '🏗️ Construction & Estimates',
    labelPl: '🏗️ Budownictwo & Kosztorys',
    labelDe: '🏗️ Bauwesen & Kalkulation',
    labelFr: '🏗️ BTP & Devis',
    flagshipIds: ['koshtorisnyk', 'contract-auditor', 'property-valuator']
  },
  {
    id: 'legal',
    iconName: 'Scale',
    labelUk: '⚖️ Юриспруденція & Договори',
    labelEn: '⚖️ Legal & Contracts',
    labelPl: '⚖️ Prawo & Umowy',
    labelDe: '⚖️ Recht & Verträge',
    labelFr: '⚖️ Droit & Contrats',
    flagshipIds: ['contract-auditor', 'commercial-contract-auditor', 'court-compliance', 'dstu-order-constructor']
  },
  {
    id: 'poland',
    iconName: 'FileCheck',
    labelUk: '🇵🇱 Польща & KSeF',
    labelEn: '🇵🇱 Poland & KSeF',
    labelPl: '🇵🇱 Polska & KSeF',
    labelDe: '🇵🇱 Polen & KSeF',
    labelFr: '🇵🇱 Pologne & KSeF',
    flagshipIds: ['ksef-vat-auditor', 'spzoo-krs-architect', 'zus-a1-delegation-specialist']
  },
  {
    id: 'us',
    iconName: 'ShieldCheck',
    labelUk: '🇺🇸 США & Delaware',
    labelEn: '🇺🇸 US & Delaware',
    labelPl: '🇺🇸 USA & Delaware',
    labelDe: '🇺🇸 USA & Delaware',
    labelFr: '🇺🇸 USA & Delaware',
    flagshipIds: ['us-delaware-corp-architect', 'us-tax-irs-advisor', 'sec-safe-counsel']
  },
  {
    id: 'agri',
    iconName: 'Satellite',
    labelUk: '🌾 Агросектор & ДЗК',
    labelEn: '🌾 Agri & Land Bank',
    labelPl: '🌾 Agrosektor & EGiB',
    labelDe: '🌾 Agrar & Kataster',
    labelFr: '🌾 Agriculture & Cadastre',
    flagshipIds: ['agri-land-consultant', 'satellite-eco-analyst', 'agrisat-monitor']
  }
];

const ROLE_PROMPTS_MAP: Record<ProfessionalRole, Partial<Record<Language, { label: string; query: string }[]>>> = {
  all: {},
  accounting: {
    uk: [
      { label: "🏛️ Розблокування ПН/РК (СМКОР)", query: "розблокування податкової накладної СМКОР пояснення таблиця даних ДПС" },
      { label: "📊 ФОП 3 група декларація ДПС", query: "звітність ФОП 3 група єдиний податок 5 відсотків книга обліку" },
      { label: "💼 Аудит видатків & КЕКВ", query: "перевірка бюджетних видатків КЕКВ казначейство публічні кошти" }
    ],
    en: [
      { label: "🏛️ Corporate Tax & VAT Audit", query: "corporate tax audit VAT reporting withholding tax deduction" },
      { label: "📊 Cross-Border VAT OSS", query: "cross-border VAT OSS EU digital services marketplace reporting" }
    ],
    pl: [
      { label: "🏛️ KSeF FA_VAT 2 & JPK_V7", query: "weryfikacja faktury KSeF FA(2) deklaracja JPK_V7 Biała Lista podatników VAT" },
      { label: "📊 CIT Estoński kalkulacja", query: "kalkulacja CIT estoński ryczałt od dochodów spółek ukryte zyski" }
    ]
  },
  construction: {
    uk: [
      { label: "🏗️ Кошторис АВК-5 (КБ-2в/КБ-3)", query: "перевірка акта КБ-2в договірна ціна АВК-5 РЕКН КНУ норми прямі витрати" },
      { label: "📋 Дефектний акт ремонту", query: "складання дефектного акта поточний ремонт покрівлі обсяги робіт" },
      { label: "📐 Журнал робіт КБ-6 за ДБН", query: "ведення загального журналу робіт КБ-6 вимоги ДБН А.3.1-5:2016" }
    ],
    en: [
      { label: "🏗️ FIDIC Interim Payment (IPC)", query: "FIDIC Red Book Interim Payment Certificate IPC variation orders" }
    ],
    pl: [
      { label: "🏗️ Kosztorys budowlany KNR / SEKOCENBUD", query: "kosztorys budowlany prace remontowe KNR przedmiar Excel SEKOCENBUD" },
      { label: "📋 Protokół odbioru robót budowlanych", query: "protokół odbioru robót budowlanych prawo budowlane wady i usterki" }
    ]
  },
  legal: {
    uk: [
      { label: "📄 Аудит договору за ДСТУ 4163", query: "аудит договору оренди приміщення за ДСТУ 4163:2020 комерційна нерухомість" },
      { label: "⚖️ Претензія про стягнення боргу", query: "досудова претензія порушення строків оплати пеня 3 проценти річних інфляційні" },
      { label: "🔒 Договір NDA конфіденційність", query: "скласти договір про нерозголошення конфіденційної інформації NDA штраф" }
    ],
    en: [
      { label: "📄 Commercial NDA & SLA Audit", query: "commercial contract audit NDA confidentiality liability clauses" }
    ],
    pl: [
      { label: "📄 Umowa B2B z programistą", query: "umowa B2B z programistą przeniesienie praw autorskich zakaz konkurencji" },
      { label: "⚖️ Wezwanie do zapłaty", query: "przedsądowe wezwanie do zapłaty odsetki ustawowe rekompensata 40 euro" }
    ]
  },
  poland: {
    uk: [
      { label: "🇵🇱 Реєстрація Sp. z o.o. (KRS S24)", query: "створення польської компанії Sp z oo статут S24 KRS капітал NIP REGON" },
      { label: "🇪🇺 Довідка ZUS A1 відрядження", query: "оформлення довідки ZUS A1 для роботи в Німеччині умова злеценя" }
    ],
    en: [
      { label: "🇵🇱 Polish Sp. z o.o. Setup & KRS", query: "registering Polish Sp z oo company KRS S24 NIP REGON articles" }
    ],
    pl: [
      { label: "🏛️ KSeF FA_VAT 2 & JPK_V7", query: "weryfikacja faktury KSeF FA(2) deklaracja JPK_V7 Biała Lista podatników VAT" },
      { label: "🏢 Rejestracja Sp. z o.o. w S24", query: "rejestracja spółki z o o w systemie S24 kapitał zakładowy CRBR" },
      { label: "🇪🇺 Zaświadczenie ZUS A1", query: "zaświadczenie ZUS A1 delegowanie pracowników do UE rozporządzenie 883/2004" }
    ]
  },
  us: {
    uk: [
      { label: "🇺🇸 Delaware C-Corp Статут & Bylaws", query: "реєстрація Delaware C-Corp статутні документи Certificate of Incorporation bylaws" },
      { label: "📑 SAFE інвестиційна угода (YC)", query: "складання угоди SAFE post-money valuation cap discount Y Combinator" },
      { label: "🏛️ FinCEN BOI звітність для іноземців", query: "FinCEN Beneficial Ownership Information BOI звітність для LLC та Corp" }
    ],
    en: [
      { label: "🇺🇸 Delaware C-Corp Charter & Bylaws", query: "Delaware C-Corp Certificate of Incorporation bylaws stock issuance 83b" },
      { label: "📑 YC Post-Money SAFE Agreement", query: "Y Combinator post-money SAFE valuation cap discount agreement" },
      { label: "🏛️ FinCEN BOI Reporting Compliance", query: "FinCEN beneficial ownership information reporting BOI foreign owners" }
    ],
    pl: [
      { label: "🇺🇸 Rejestracja Delaware LLC / C-Corp", query: "rejestracja spółki Delaware LLC C-Corp EIN ITIN umowa operacyjna" }
    ]
  },
  agri: {
    uk: [
      { label: "🌾 Аудит накладок ділянок ДЗК", query: "перевірка накладок земельних ділянок публічна кадастрова карта ДЗК обмінні файли IN4 XML" },
      { label: "🛰️ Супутниковий моніторинг NDVI полів", query: "супутниковий моніторинг NDVI полів Sentinel-2 Maxar 0.3m ДАР" },
      { label: "💰 Нормативна грошова оцінка (НГО)", query: "розрахунок земельного податку коефіцієнт індексації НГО витяг з ДЗК" }
    ],
    en: [
      { label: "🌾 Satellite Crop & NDVI Health", query: "satellite NDVI field monitoring Sentinel-2 Maxar 0.3m agriculture" }
    ],
    pl: [
      { label: "🌾 Weryfikacja działek w Geoportal EGiB", query: "weryfikacja granic działek rolnych Geoportal EGiB nakładanie granic" }
    ]
  }
};

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Bot className={className} />;
  return <IconComponent className={className} />;
};

export default function SmartAgentDispatcher({
  onLaunchAgent,
  language,
  currentUserEmail,
  enterpriseName,
  hasEnterprise,
  onOpenEnterpriseDashboard
}: SmartAgentDispatcherProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isDe = language === 'de';
  const isFr = language === 'fr';
  const isIt = language === 'it';
  const isEs = language === 'es';
  const isPt = language === 'pt';
  const isSv = language === 'sv';
  const isEn = language === 'en';

  const [query, setQuery] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recommendations, setRecommendations] = useState<RecommendedAgent[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isMissingAgent, setIsMissingAgent] = useState(false);
  const [isGreeting, setIsGreeting] = useState(false);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isGeneratingDynamicAgent, setIsGeneratingDynamicAgent] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [showNormativeDrawer, setShowNormativeDrawer] = useState(false);

  // Omni-Input enhancements
  const [selectedRole, setSelectedRole] = useState<ProfessionalRole>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachedFile, setAttachedFile] = useState<{ name: string; size: number; text: string } | null>(null);
  const [isExtractingFile, setIsExtractingFile] = useState(false);
  const [fileExtractError, setFileExtractError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Scenario 2: Autonomous On-Demand Module Synthesis & Global Publication
  const [availableAgents, setAvailableAgents] = useState<any[]>(() => {
    return (agentsConfig as any[]).filter(
      a => a.id !== 'enterprise-dashboard' && a.id !== 'admin-lead-harvester'
    );
  });
  const [isSynthesizingPermanent, setIsSynthesizingPermanent] = useState(false);
  const [synthesizeStep, setSynthesizeStep] = useState(0);
  const [createdPermanentModule, setCreatedPermanentModule] = useState<any | null>(null);
  const [createdTaskId, setCreatedTaskId] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/agents-config')
      .then(res => res.json())
      .then(data => {
        if (data.ok && Array.isArray(data.agents)) {
          setAvailableAgents(data.agents.filter(
            (a: any) => a.id !== 'enterprise-dashboard' && a.id !== 'admin-lead-harvester'
          ));
        }
      })
      .catch(() => {});
  }, []);

  const quickPrompts = QUICK_PROMPTS_MAP[language] || QUICK_PROMPTS_MAP.uk;
  const normativePresets = NORMATIVE_PRESETS_MAP[language] || NORMATIVE_PRESETS_MAP.uk;

  const updateFlagshipsForRole = (role: ProfessionalRole) => {
    const allAgents = availableAgents;
    
    const roleCfg = ROLE_TABS.find(r => r.id === role) || ROLE_TABS[0];
    let targetIds = roleCfg.flagshipIds;

    // If role is 'all', check regional flags
    if (role === 'all') {
      if (isPl) {
        targetIds = ['ksef-vat-auditor', 'spzoo-krs-architect', 'zus-a1-delegation-specialist'];
      } else if (isDe || isFr || isEn) {
        targetIds = ['cbam-supply-chain-carbon-auditor', 'contract-auditor', 'us-delaware-corp-architect'];
      } else {
        targetIds = ['contract-auditor', 'dstu-pkmu-auditor', 'construction-acceptance-auditor'];
      }
    }

    const defaultRecs: RecommendedAgent[] = [];
    targetIds.forEach(id => {
      const found = allAgents.find(a => a.id === id);
      if (found) {
        defaultRecs.push({
          id: found.id,
          nameUk: found.nameUk,
          nameEn: found.nameEn,
          namePl: found.namePl,
          nameDe: found.nameDe,
          nameFr: found.nameFr,
          nameIt: found.nameIt,
          nameEs: found.nameEs,
          namePt: found.namePt,
          nameSv: found.nameSv,
          descriptionUk: found.descriptionUk,
          descriptionEn: found.descriptionEn,
          descriptionPl: found.descriptionPl,
          descriptionDe: found.descriptionDe,
          descriptionFr: found.descriptionFr,
          descriptionIt: found.descriptionIt,
          descriptionEs: found.descriptionEs,
          descriptionPt: found.descriptionPt,
          descriptionSv: found.descriptionSv,
          icon: found.icon || 'Briefcase',
          categoryUk: found.categoryUk || 'ГАЛУЗЕВИЙ ХАБ',
          categoryEn: found.categoryEn,
          categoryPl: found.categoryPl,
          categoryDe: found.categoryDe,
          categoryFr: found.categoryFr,
          categoryIt: found.categoryIt,
          categoryEs: found.categoryEs,
          categoryPt: found.categoryPt,
          categorySv: found.categorySv,
          matchReasonUk: 'Флагманський нормативний модуль',
          matchReasonEn: 'Flagship normative module',
          matchReasonPl: 'Flagowy moduł normatywny',
          matchReasonDe: 'Flaggschiff-Normmodul',
          matchReasonFr: 'Module normatif phare',
          matchReasonIt: 'Modulo normativo di punta',
          matchReasonEs: 'Módulo normativo principal',
          matchReasonPt: 'Módulo normativo principal',
          matchReasonSv: 'Flaggskeppsmodul',
          score: 100
        });
      }
    });

    if (defaultRecs.length > 0) {
      setRecommendations(defaultRecs);
    }
  };

  // Auto-populate regional flagship recommendations on initial load, language, or role switch
  useEffect(() => {
    if (!hasSearched) {
      updateFlagshipsForRole(selectedRole);
    }
  }, [language, hasSearched, selectedRole, isPl, isDe, isFr, isEn]);

  const handleRoleSelect = (role: ProfessionalRole) => {
    setSelectedRole(role);
    if (!hasSearched) {
      updateFlagshipsForRole(role);
    }
  };

  const handleProcessFile = async (file: File) => {
    if (!file) return;
    setIsExtractingFile(true);
    setFileExtractError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || (isUk ? 'Помилка вилучення тексту з документа' : 'Error extracting text from file'));
      }

      const data = await res.json();
      setAttachedFile({
        name: data.name || file.name,
        size: data.size || file.size,
        text: data.text || ''
      });

      if (!query.trim()) {
        setQuery(
          isUk
            ? `Проаналізувати прикріплений документ: ${data.name || file.name}`
            : isPl
            ? `Przeanalizuj załączony dokument: ${data.name || file.name}`
            : `Analyze attached document: ${data.name || file.name}`
        );
      }
    } catch (err: any) {
      console.error('File extraction error:', err);
      setFileExtractError(err.message || (isUk ? 'Помилка читання файлу' : 'Error reading file'));
    } finally {
      setIsExtractingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleProcessFile(file);
  };

  const isGibberishOrSpam = (text: string): boolean => {
    if (!text || typeof text !== 'string') return true;
    const trimmed = text.trim();
    if (trimmed.length < 3) return true;
    if (/(.)\1{3,}/i.test(trimmed)) return true;
    if (/(qwerty|asdfgh|zxcvbn|йцукен|фывапр|ячсмить)/i.test(trimmed) && trimmed.length < 20) return true;
    const letters = (trimmed.match(/[a-zA-Zа-яА-ЯіІїЇєЄґҐąćęłńóśźżĄĆĘŁŃÓŚŹŻäöüßéèêàâçîïôûùÄÖÜÉÈÊÀÂÇÎÏÔÛÙáéíóúÁÉÍÓÚãõÃÕåäöÅÄÖ]/g) || []).length;
    const vowels = (trimmed.match(/[аеєиіїоуюяaeiouyАЕЄИІЇОУЮЯAEIOUYąęóĄĘÓäöüéèêàâîïôûùÄÖÜÉÈÊÀÂÎÏÔÛÙáéíóúÁÉÍÓÚãõÃÕåäöÅÄÖ]/g) || []).length;
    if (letters > 5 && vowels === 0) return true;
    return false;
  };

  const notifyAdminTelegram = async (userQueryText: string) => {
    if (isGibberishOrSpam(userQueryText)) {
      console.log('Skipping Telegram alert for gibberish/spam query:', userQueryText);
      return;
    }
    try {
      const res = await fetch('/api/notify-missing-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: userQueryText,
          userQuery: userQueryText,
          language,
          userEmail: currentUserEmail || (isUk ? 'Анонімний відвідувач (Сторінка Входу)' : 'Anonymous visitor (Login page)'),
          timestamp: new Date().toISOString()
        })
      });
      if (res.ok) {
        setRequestSent(true);
      }
    } catch (err) {
      console.error('Failed to notify admin via Telegram:', err);
    }
  };

  const handleManualSendTelegramRequest = async () => {
    if (!query.trim()) return;
    setIsSendingRequest(true);
    try {
      await notifyAdminTelegram(query);
      setRequestSent(true);
    } finally {
      setIsSendingRequest(false);
    }
  };

  const handleGenerateDynamicAgent = async () => {
    if (!query.trim()) return;
    setIsGeneratingDynamicAgent(true);
    try {
      const res = await fetch('/api/generate-dynamic-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          language,
          userEmail: currentUserEmail || (isUk ? 'Анонімний відвідувач (Сторінка Входу)' : 'Anonymous user')
        })
      });

      const data = await res.json();
      if (res.ok && data.ok && data.agent) {
        if (onLaunchAgent) {
          onLaunchAgent('dynamic-on-the-fly-agent', language, undefined, data.agent);
        }
      } else {
        alert(data.message || (isUk ? 'Не вдалося згенерувати модуль.' : 'Failed to generate agent.'));
      }
    } catch (err) {
      console.error('Error generating dynamic agent:', err);
      alert(isUk ? 'Помилка генерації модуля на льоту' : 'Error generating on-the-fly module');
    } finally {
      setIsGeneratingDynamicAgent(false);
    }
  };

  const handleCreatePermanentModule = async () => {
    const currentTask = query.trim() || (attachedFile ? `Аналіз прикріпленого документа ${attachedFile.name}` : '');
    if (!currentTask && !attachedFile) {
      alert(
        isDe ? 'Bitte geben Sie eine Aufgabe ein oder laden Sie ein Dokument hoch.' :
        isFr ? 'Veuillez saisir une tâche ou joindre un document.' :
        isPl ? 'Wpisz treść zadania lub załącz dokument do analizy.' :
        isUk ? 'Будь ласка, опишіть завдання або прикріпіть документ до аналізу.' :
        'Please describe your task or attach a document.'
      );
      return;
    }

    setIsSynthesizingPermanent(true);
    setSynthesizeStep(0);
    setCreatedPermanentModule(null);

    const stepInterval = setInterval(() => {
      setSynthesizeStep(prev => (prev < 3 ? prev + 1 : prev));
    }, 3200);

    try {
      const res = await fetch('/api/request-custom-module', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: currentTask,
          taskDescription: currentTask,
          attachedFile: attachedFile ? { name: attachedFile.name, text: attachedFile.text, size: attachedFile.size } : undefined,
          language,
          userEmail: currentUserEmail || '',
          enterpriseName: enterpriseName || '',
          selectedRole
        })
      });

      const data = await res.json();
      if (res.ok && data.ok && data.agent) {
        clearInterval(stepInterval);
        setSynthesizeStep(3);
        setCreatedPermanentModule(data.agent);
        setCreatedTaskId(data.taskId);

        // Prepend to availableAgents so it's immediately searchable & scoreable in this session
        setAvailableAgents(prev => [data.agent, ...prev]);

        // Prefill intent in sessionStorage so it opens with all parameters
        try {
          sessionStorage.setItem('drh_intent_prefill', JSON.stringify({
            text: currentTask,
            taskText: currentTask,
            taskDescription: currentTask,
            companyName: enterpriseName || '',
            enterpriseName: enterpriseName || '',
            attachedFile: attachedFile ? { name: attachedFile.name, text: attachedFile.text } : undefined,
            extractedText: attachedFile ? attachedFile.text : undefined,
            fileName: attachedFile ? attachedFile.name : undefined,
            selectedRole
          }));
        } catch (e) {
          console.warn('Error setting drh_intent_prefill:', e);
        }
      } else {
        clearInterval(stepInterval);
        alert(data.error || (isUk ? 'Помилка створення модуля.' : 'Error creating module.'));
      }
    } catch (err) {
      clearInterval(stepInterval);
      console.error('Error creating permanent module:', err);
      alert(isUk ? 'Не вдалося створити модуль на сервері.' : 'Failed to create module on server.');
    } finally {
      setIsSynthesizingPermanent(false);
    }
  };

  const isSimpleGreeting = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    const cleaned = text
      .toLowerCase()
      .trim()
      .replace(/[!?,.:;~@#$%^&*()_+=\-\/\\|`'"<>]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!cleaned) return false;

    const greetings = [
      // UK
      'привіт', 'привітик', 'добрий день', 'доброго дня', 'добрий ранок', 'доброго ранку', 'добрий вечір', 'доброго вечора', 'вітаю', 'здоров', 'здоровенькі були', 'привіт усім', 'привіт всім', 'шановні',
      // RU
      'привет', 'приветик', 'здравствуйте', 'здравствуй', 'добрый день', 'доброе утро', 'добрый вечер', 'доброго времени суток', 'приветствую', 'хай', 'салют', 'ку', 'здрасте', 'привет всем',
      // EN
      'hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening', 'good day', 'howdy', 'what s up', 'whats up', 'sup', 'hello there', 'hi there', 'hey there', 'greetings', 'welcome',
      // PL
      'cześć', 'czesc', 'dzień dobry', 'dzien dobry', 'witam', 'hej', 'hejka', 'siema', 'siemanko', 'dobry wieczór', 'dobry wieczor', 'witaj', 'witajcie',
      // DE
      'hallo', 'guten tag', 'guten morgen', 'guten abend', 'servus', 'grüß gott', 'gruss gott', 'moin', 'moin moin', 'tag',
      // FR
      'bonjour', 'salut', 'bonsoir', 'coucou', 'bienvenue', 'allô', 'allo',
      // IT
      'ciao', 'buongiorno', 'buonasera', 'salve', 'buondì', 'buondi',
      // ES
      'hola', 'buenos días', 'buenos dias', 'buenas tardes', 'buenas noches', 'buenas', 'saludos',
      // PT
      'olá', 'ola', 'bom dia', 'boa tarde', 'boa noite', 'oi',
      // SV
      'hej', 'hallå', 'halla', 'god morgon', 'god dag', 'god kväll', 'god kvall', 'tjena', 'hejsan'
    ];

    return greetings.some(g => cleaned === g || cleaned.startsWith(g + ' ') || cleaned.endsWith(' ' + g));
  };

  const scoreAgents = (searchQuery: string): RecommendedAgent[] => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return [];

    const allAgents = availableAgents;

    const scored: RecommendedAgent[] = allAgents.map(agent => {
      let score = 0;
      let reasonUk = '';
      let reasonEn = '';
      let reasonPl = '';
      let reasonFr = '';
      let reasonDe = '';
      let reasonIt = '';
      let reasonEs = '';
      let reasonPt = '';
      let reasonSv = '';

      const nameUk = (agent.nameUk || '').toLowerCase();
      const nameEn = (agent.nameEn || '').toLowerCase();
      const namePl = (agent.namePl || '').toLowerCase();
      const nameFr = (agent.nameFr || '').toLowerCase();
      const nameDe = (agent.nameDe || '').toLowerCase();
      const nameIt = (agent.nameIt || '').toLowerCase();
      const nameEs = (agent.nameEs || '').toLowerCase();
      const namePt = (agent.namePt || '').toLowerCase();
      const nameSv = (agent.nameSv || '').toLowerCase();

      const descUk = (agent.descriptionUk || '').toLowerCase();
      const descEn = (agent.descriptionEn || '').toLowerCase();
      const descPl = (agent.descriptionPl || '').toLowerCase();
      const descFr = (agent.descriptionFr || '').toLowerCase();
      const descDe = (agent.descriptionDe || '').toLowerCase();
      const descIt = (agent.descriptionIt || '').toLowerCase();
      const descEs = (agent.descriptionEs || '').toLowerCase();
      const descPt = (agent.descriptionPt || '').toLowerCase();
      const descSv = (agent.descriptionSv || '').toLowerCase();

      const catUk = (agent.categoryUk || '').toLowerCase();
      const catEn = (agent.categoryEn || '').toLowerCase();
      const catPl = (agent.categoryPl || '').toLowerCase();
      const catFr = (agent.categoryFr || '').toLowerCase();
      const catDe = (agent.categoryDe || '').toLowerCase();
      const catIt = (agent.categoryIt || '').toLowerCase();
      const catEs = (agent.categoryEs || '').toLowerCase();
      const catPt = (agent.categoryPt || '').toLowerCase();
      const catSv = (agent.categorySv || '').toLowerCase();

      const tokens = q.split(/[\s,.;:!?/\-()]+/).filter(t => t.length > 2);

      tokens.forEach(token => {
        if (
          nameUk.includes(token) || nameEn.includes(token) || namePl.includes(token) || 
          nameFr.includes(token) || nameDe.includes(token) || nameIt.includes(token) || 
          nameEs.includes(token) || namePt.includes(token) || nameSv.includes(token)
        ) {
          score += 15;
          reasonUk = `Прямий збіг за назвою модуля (${token})`;
          reasonEn = `Direct match on module name (${token})`;
          reasonPl = `Bezpośrednie dopasowanie nazwy (${token})`;
          reasonFr = `Correspondance directe avec le nom (${token})`;
          reasonDe = `Direkte Übereinstimmung im Modulnamen (${token})`;
          reasonIt = `Corrispondenza diretta per nome modulo (${token})`;
          reasonEs = `Coincidencia directa con el nombre (${token})`;
          reasonPt = `Correspondência direta com o nome (${token})`;
          reasonSv = `Direkt matchning mot modulnamn (${token})`;
        }
        if (
          descUk.includes(token) || descEn.includes(token) || descPl.includes(token) || 
          descFr.includes(token) || descDe.includes(token) || descIt.includes(token) || 
          descEs.includes(token) || descPt.includes(token) || descSv.includes(token)
        ) {
          score += 6;
          if (!reasonUk) {
            reasonUk = `Відповідність функції та задачі (${token})`;
            reasonEn = `Functionality match (${token})`;
            reasonPl = `Dopasowanie funkcjonalne (${token})`;
            reasonFr = `Correspondance fonctionnelle (${token})`;
            reasonDe = `Funktionale Übereinstimmung (${token})`;
            reasonIt = `Corrispondenza funzionale (${token})`;
            reasonEs = `Coincidencia funcional (${token})`;
            reasonPt = `Correspondência funcional (${token})`;
            reasonSv = `Funktionell matchning (${token})`;
          }
        }
        if (
          catUk.includes(token) || catEn.includes(token) || catPl.includes(token) || 
          catFr.includes(token) || catDe.includes(token) || catIt.includes(token) || 
          catEs.includes(token) || catPt.includes(token) || catSv.includes(token)
        ) {
          score += 4;
          if (!reasonUk) {
            reasonUk = `Співпадіння галузевої категорії (${token})`;
            reasonEn = `Industry category match (${token})`;
            reasonPl = `Dopasowanie branżowe (${token})`;
            reasonFr = `Correspondance sectorielle (${token})`;
            reasonDe = `Übereinstimmung der Branchenkategorie (${token})`;
            reasonIt = `Corrispondenza di categoria settoriale (${token})`;
            reasonEs = `Coincidencia de categoría sectorial (${token})`;
            reasonPt = `Correspondência de categoria setorial (${token})`;
            reasonSv = `Matchning av branschkategori (${token})`;
          }
        }
      });

      if (!reasonUk) {
        reasonUk = 'Висока галузева релевантність';
        reasonEn = 'High domain relevance';
        reasonPl = 'Wysoka zgodność dziedzinowa';
        reasonFr = 'Forte pertinence sectorielle';
        reasonDe = 'Hohe fachliche Relevanz';
        reasonIt = 'Alta rilevanza di settore';
        reasonEs = 'Alta relevancia sectorial';
        reasonPt = 'Alta relevância setorial';
        reasonSv = 'Hög branschrelevans';
      }

      return {
        id: agent.id,
        nameUk: agent.nameUk,
        nameEn: agent.nameEn,
        namePl: agent.namePl,
        nameFr: agent.nameFr,
        nameDe: agent.nameDe,
        nameIt: agent.nameIt,
        nameEs: agent.nameEs,
        namePt: agent.namePt,
        nameSv: agent.nameSv,
        descriptionUk: agent.descriptionUk,
        descriptionEn: agent.descriptionEn,
        descriptionPl: agent.descriptionPl,
        descriptionFr: agent.descriptionFr,
        descriptionDe: agent.descriptionDe,
        descriptionIt: agent.descriptionIt,
        descriptionEs: agent.descriptionEs,
        descriptionPt: agent.descriptionPt,
        descriptionSv: agent.descriptionSv,
        icon: agent.icon || 'Bot',
        categoryUk: agent.categoryUk,
        categoryEn: agent.categoryEn,
        categoryPl: agent.categoryPl,
        categoryFr: agent.categoryFr,
        categoryDe: agent.categoryDe,
        categoryIt: agent.categoryIt,
        categoryEs: agent.categoryEs,
        categoryPt: agent.categoryPt,
        categorySv: agent.categorySv,
        matchReasonUk: reasonUk,
        matchReasonEn: reasonEn,
        matchReasonPl: reasonPl,
        matchReasonFr: reasonFr,
        matchReasonDe: reasonDe,
        matchReasonIt: reasonIt,
        matchReasonEs: reasonEs,
        matchReasonPt: reasonPt,
        matchReasonSv: reasonSv,
        score
      };
    });

    return scored.filter(s => s.score > 0).sort((a, b) => b.score - a.score).slice(0, 3);
  };

  const performMatching = async (searchQuery: string, sendAlertIfMissing: boolean = true) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q && !attachedFile) {
      setRecommendations([]);
      setHasSearched(false);
      setIsMissingAgent(false);
      setIsGreeting(false);
      return;
    }

    setIsAnalyzing(true);
    setHasSearched(true);
    setRequestSent(false);

    if (q && isSimpleGreeting(q)) {
      setIsGreeting(true);
      setIsMissingAgent(false);
      setRecommendations([]);
      setIsAnalyzing(false);
      return;
    } else {
      setIsGreeting(false);
    }

    const topMatches = scoreAgents(searchQuery);
    setRecommendations(topMatches);

    if (topMatches.length === 0) {
      setIsMissingAgent(true);
      if (sendAlertIfMissing) {
        notifyAdminTelegram(searchQuery);
      }
    } else {
      setIsMissingAgent(false);
    }

    setIsAnalyzing(false);
  };

  const handleDirectExecute = async () => {
    const currentTask = query.trim() || (attachedFile ? `Аналіз прикріпленого документа ${attachedFile.name}` : '');
    if (!currentTask && !attachedFile) {
      alert(
        isDe ? 'Bitte geben Sie eine Aufgabe ein oder laden Sie ein Dokument hoch.' :
        isFr ? 'Veuillez saisir une tâche ou joindre un document.' :
        isPl ? 'Wpisz treść zadania lub załącz dokument do analizy.' :
        isUk ? 'Будь ласка, опишіть завдання або прикріпіть документ до аналізу.' :
        'Please describe your task or attach a document.'
      );
      return;
    }

    const prefillData = {
      text: currentTask,
      taskText: currentTask,
      taskDescription: currentTask,
      companyName: enterpriseName || '',
      enterpriseName: enterpriseName || '',
      attachedFile: attachedFile ? { name: attachedFile.name, text: attachedFile.text } : undefined,
      extractedText: attachedFile ? attachedFile.text : undefined,
      fileName: attachedFile ? attachedFile.name : undefined,
      selectedRole
    };

    try {
      sessionStorage.setItem('drh_intent_prefill', JSON.stringify(prefillData));
    } catch (e) {
      console.warn('Error setting drh_intent_prefill:', e);
    }

    setIsAnalyzing(true);
    setHasSearched(true);
    const matches = scoreAgents(currentTask);

    if (matches.length > 0) {
      setIsAnalyzing(false);
      onLaunchAgent(matches[0].id, language);
      return;
    }

    // Scenario 2: Autonomous On-Demand Module Creation & Global Publication
    setIsMissingAgent(true);
    setIsAnalyzing(false);
    await handleCreatePermanentModule();
  };

  const handleLaunchWithIntent = (agentId: string) => {
    const currentTask = query.trim() || (attachedFile ? `Аналіз прикріпленого документа ${attachedFile.name}` : '');
    if (currentTask || attachedFile || enterpriseName) {
      try {
        sessionStorage.setItem('drh_intent_prefill', JSON.stringify({
          text: currentTask,
          taskText: currentTask,
          taskDescription: currentTask,
          companyName: enterpriseName || '',
          enterpriseName: enterpriseName || '',
          attachedFile: attachedFile ? { name: attachedFile.name, text: attachedFile.text } : undefined,
          extractedText: attachedFile ? attachedFile.text : undefined,
          fileName: attachedFile ? attachedFile.name : undefined,
          selectedRole
        }));
      } catch (e) {
        console.warn('Error setting drh_intent_prefill:', e);
      }
    }
    onLaunchAgent(agentId, language);
  };

  const handleNormativeItemSelect = (itemQuery: string) => {
    setQuery(itemQuery);
    setShowNormativeDrawer(false);
    performMatching(itemQuery, true);
  };

  const handleQuickPromptClick = (promptQuery: string) => {
    setQuery(promptQuery);
    performMatching(promptQuery, true);
  };

  const handleClear = () => {
    setQuery('');
    setRecommendations([]);
    setHasSearched(false);
    setIsMissingAgent(false);
    setIsGreeting(false);
    setRequestSent(false);
  };

  return (
    <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 border border-blue-500/30 rounded-2xl p-3.5 sm:p-6 md:p-8 text-white shadow-2xl relative overflow-hidden font-mono space-y-4 sm:space-y-6 w-full max-w-full">
      <div className="absolute -right-20 -top-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

      {/* 0. Enterprise Context Link Banner */}
      <div className="p-3.5 sm:p-4 bg-gradient-to-r from-blue-950/90 via-slate-900 to-indigo-950/90 border border-blue-500/40 rounded-2xl flex items-center justify-between flex-wrap gap-3 z-10 relative shadow-xl">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-blue-600/30 border border-blue-400/40 rounded-xl text-blue-300 shrink-0">
            <Building2 className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-bold tracking-wider text-blue-300">
                {isUk ? '🏢 КОРПОРАТИВНИЙ ПРОФІЛЬ ПІДПРИЄМСТВА' : isPl ? '🏢 PROFIL KORPORACYJNY PRZEDSIĘBIORSTWA' : '🏢 ENTERPRISE CORPORATE PROFILE'}
              </span>
              {enterpriseName ? (
                <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 text-[9px] font-extrabold rounded">
                  ✓ {isUk ? 'Підключено' : isPl ? 'Połączono' : 'Connected'}
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-400/30 text-[9px] font-bold rounded">
                  {isUk ? 'Не підключено' : isPl ? 'Niepołączony' : 'Not linked'}
                </span>
              )}
            </div>
            <div className="text-xs sm:text-sm font-extrabold text-white truncate mt-0.5">
              {enterpriseName 
                ? enterpriseName 
                : (isUk ? 'Кабінет Підприємства не підключений' : isPl ? 'Panel Przedsiębiorstwa niepołączony' : 'Enterprise Dashboard not connected')}
            </div>
            <div className="text-[10px] text-slate-300 font-mono hidden sm:block">
              {enterpriseName 
                ? (isUk ? 'Реквізити компанії та ЄДРПОУ автоматично підставляються в усі задачі' : isPl ? 'Dane firmy i NIP są automatycznie dołączane do zadań' : 'Company profile and tax IDs are injected into tasks')
                : (isUk ? 'Підключіть реквізити компанії в Кабінеті для автозаповнення в 326+ модулях' : isPl ? 'Skonfiguruj firmę w panelu dla autouzupełniania' : 'Connect company profile for auto-fill in all modules')}
            </div>
          </div>
        </div>

        {onOpenEnterpriseDashboard && (
          <button
            onClick={onOpenEnterpriseDashboard}
            className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer shrink-0 border border-blue-400/30 hover:scale-[1.02]"
          >
            <span>{enterpriseName ? (isUk ? '🏢 Відкрити Кабінет' : isPl ? '🏢 Otwórz Panel' : '🏢 Open Dashboard') : (isUk ? '➕ Налаштувати' : isPl ? '➕ Skonfiguruj' : '➕ Setup')}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* 1. Professional Role Tabs Bar */}
      <div className="space-y-1.5 z-10 relative">
        <div className="text-[10px] font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
          <Layers className="w-3.5 h-3.5 text-blue-400" />
          <span>{isUk ? 'Фільтр за професійними ролями & сферами:' : isPl ? 'Filtr według ról zawodowych & branż:' : 'Filter by professional role & domain:'}</span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {ROLE_TABS.map((role) => {
            const isActive = selectedRole === role.id;
            const label = isUk ? role.labelUk : isPl ? role.labelPl : isDe ? role.labelDe : isFr ? role.labelFr : role.labelEn;
            return (
              <button
                key={role.id}
                onClick={() => handleRoleSelect(role.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 whitespace-nowrap cursor-pointer shrink-0 border ${
                  isActive 
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-400 shadow-md shadow-blue-500/30' 
                    : 'bg-slate-900/80 text-slate-300 hover:text-white border-slate-700/80 hover:border-slate-500'
                }`}
              >
                <AgentIcon name={role.iconName} className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap z-10 relative">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-500/20 border border-blue-400/30 rounded-xl text-blue-400 shrink-0">
            <Bot className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-extrabold uppercase tracking-wide flex items-center gap-2 flex-wrap">
              <span className="text-white drop-shadow">
                {isDe
                  ? '🔍 WELCHE AUFGABE MÖCHTEN SIE LÖSEN, PRÜFEN ODER ERSTELLEN?'
                  : isFr
                  ? '🔍 QUELLE TÂCHE SOUHAITEZ-VOUS RÉSOUDRE, VÉRIFIER OU RÉDIGER ?'
                  : isIt
                  ? '🔍 QUALE ATTIVITÀ VUOI RISOLVERE, VERIFICARE O REDIGERE?'
                  : isEs
                  ? '🔍 ¿QUÉ TAREA DESEA RESOLVER, VERIFICAR O REDACTAR?'
                  : isPt
                  ? '🔍 QUE TAREFA DESEJA RESOLVER, VERIFICAR OU ELABORAR?'
                  : isSv
                  ? '🔍 VILKEN UPPGIFT VILL DU LÖSA, GRANSKA ELLER UPPRÄTTA?'
                  : isPl 
                  ? '🔍 JAKIE ZADANIE CHCESZ ROZWIĄZAĆ, ZWERYFIKOWAĆ LUB SPORZĄDZIĆ DOKUMENT?' 
                  : isUk 
                  ? '🔍 ЯКУ ЗАДАЧУ ТРЕБА ВИРІШИТИ, ПЕРЕВІРИТИ АБО СКЛАСТИ ДОКУМЕНТ?' 
                  : '🔍 WHAT TASK DO YOU NEED RESOLVED, CHECKED, OR DRAFTED?'}
              </span>
              <span className="px-2 py-0.5 text-[9px] bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded uppercase font-bold tracking-wider">
                {isDe 
                  ? 'Smart Dispatcher 2.0 (BGB / HGB / DIN / XRechnung)'
                  : isFr
                  ? 'Smart Dispatcher 2.0 (Normes / Factur-X / Droit)'
                  : isIt
                  ? 'Smart Dispatcher 2.0 (FatturaPA / F24 / Codice Appalti)'
                  : isEs
                  ? 'Smart Dispatcher 2.0 (FacturaE / Modelo 303 / CTE)'
                  : isPt
                  ? 'Smart Dispatcher 2.0 (SAF-T PT / ATCUD / CCP)'
                  : isSv
                  ? 'Smart Dispatcher 2.0 (PEPPOL / AB 04 / Skatteverket)'
                  : isPl 
                  ? 'Smart Dispatcher 2.0 (Normy / KSeF / Prawo)' 
                  : isUk 
                  ? 'Smart Dispatcher 2.0 (ДСТУ / ПКУ / ДБН)' 
                  : 'Smart Dispatcher 2.0 (Standards / Law / Tax)'}
              </span>
            </h2>
            <p className="text-xs text-slate-300 mt-1 max-w-xl">
              {isDe
                ? 'Beschreiben Sie Ihre Aufgabe in einfachen Worten oder wählen Sie eine Rechtsnorm aus dem Menü, um sofort das passende KI-Expertenmodul zu starten!'
                : isFr
                ? 'Décrivez votre tâche en langage naturel ou choisissez une norme juridique pour trouver instantanément le module IA adapté !'
                : isIt
                ? 'Descrivi la tua attività in parole semplici o scegli uno standard normativo per avviare subito il modulo esperto dedicato!'
                : isEs
                ? '¡Describa su tarea en lenguaje natural o seleccione una norma legal para iniciar al instante el módulo de IA especializado!'
                : isPt
                ? 'Descreva a sua tarefa em linguagem natural ou selecione uma norma legal para iniciar imediatamente o módulo especialista adequado!'
                : isSv
                ? 'Beskriv din uppgift i klartext eller välj en branschstandard för att omedelbart starta rätt expertmodul!'
                : isPl
                ? 'Opisz zadanie prostymi słowami lub skorzystaj z podpowiedzi normatywnych, aby błyskawicznie dobrać odpowiedniego agenta AI!'
                : isUk 
                ? 'Опишіть завдання людською мовою або скористайтесь нормативним підказчиком (ДСТУ 4163, ПКУ, РЕКН КНУ) для миттєвого підбору потрібного ШІ-агента!'
                : 'Describe your task or pick a normative standard (DSTU, Tax, Codes) to match the exact AI solution!'}
            </p>
          </div>
        </div>

        {/* Normative Selector Button */}
        <button
          onClick={() => setShowNormativeDrawer(!showNormativeDrawer)}
          className="px-3.5 py-2 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white font-extrabold text-xs uppercase rounded-xl border border-emerald-400/40 transition-all shadow-md flex items-center gap-2 cursor-pointer z-10"
        >
          <BookOpen className="w-4 h-4 text-emerald-200" />
          <span>
            {isDe
              ? '📜 Normativer Assistent (BGB / VOB / LkSG)'
              : isFr
              ? '📜 Assistant Normatif (Code / Factur-X / BTP)'
              : isIt
              ? '📜 Assistente Normativo (FatturaPA / Codici)'
              : isEs
              ? '📜 Asistente Normativo (FacturaE / AEAT)'
              : isPt
              ? '📜 Assistente Normativo (SAF-T / AT)'
              : isSv
              ? '📜 Normativ Assistent (PEPPOL / Skatt)'
              : isPl 
              ? '📜 Podpowiedzi Normatywne (Prawo / KSeF / KNR)' 
              : isUk 
              ? '📜 Нормативний Підказчик (ДСТУ / ПКУ / ДБН)' 
              : '📜 Normative Selector (Standards / Tax / Codes)'}
          </span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showNormativeDrawer ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Normative Combobox Drawer */}
      {showNormativeDrawer && (
        <div className="p-4 sm:p-5 bg-slate-950/95 border border-emerald-500/40 rounded-xl space-y-4 animate-fade-in z-20 relative shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-800 pb-2">
            <h4 className="text-xs font-extrabold text-emerald-300 uppercase tracking-wide flex items-center gap-2">
              <Layers className="w-4 h-4 text-emerald-400" />
              <span>
                {isDe
                  ? 'RECHTSNORMEN, VORSCHRIFTEN & DEUTSCHE STANDARDS:'
                  : isFr
                  ? 'NORMES LÉGALES, CODES ET RÉGLEMENTATIONS :'
                  : isIt
                  ? 'NORMATIVE, CODICI E STANDARD ITALIANI ED EUROPEI:'
                  : isEs
                  ? 'NORMAS LEGALES, CÓDIGOS Y ESTÁNDARES ESPAÑOLES Y DE LA UE:'
                  : isPt
                  ? 'NORMAS LEGAIS, CÓDIGOS E PADRÕES PORTUGUESES E DA UE:'
                  : isSv
                  ? 'RÄTTSLIGA NORMER, LAGKRAV OCH SVENSKA STANDARDER:'
                  : isPl 
                  ? 'NORMY PRAWNE, PRZEPISY I KODEKSY:' 
                  : isUk 
                  ? 'ВИНАДПОТУЖНІ НОРМАТИВНІ ШАБЛОНИ ТА КОДЕКСИ УКРАЇНИ:' 
                  : 'NORMATIVE CODES & INDUSTRY STANDARDS:'}
              </span>
            </h4>
            <span className="text-[10px] text-slate-400 font-mono">
              {isDe
                ? 'Klicken Sie auf einen Eintrag zur automatischen Übernahme & Ausführung'
                : isFr
                ? 'Cliquez sur un élément pour remplir et lancer automatiquement'
                : isIt
                ? 'Fai clic su una voce per la compilazione e l\'avvio automatico'
                : isEs
                ? 'Haga clic en un elemento para autorrellenar e iniciar'
                : isPt
                ? 'Clique num item para autopreencher e iniciar'
                : isSv
                ? 'Klicka på ett alternativ för att autofylla och starta'
                : isPl 
                ? 'Kliknij pozycję, aby automatycznie wypełnić i uruchomić' 
                : isUk 
                ? 'Клацніть на пункт для автозаповнення та запуску' 
                : 'Click to auto-fill & run'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {normativePresets.map((group, idx) => (
              <div key={idx} className="space-y-2 bg-slate-900/80 p-3 rounded-lg border border-slate-800 hover:border-emerald-500/30 transition-colors">
                <div className="text-[11px] font-extrabold text-emerald-300 uppercase tracking-wider flex items-center gap-1.5 pb-1 border-b border-slate-800">
                  <span>{group.category}</span>
                </div>
                <div className="space-y-1.5 pt-1">
                  {(group.items || []).map((item, itemIdx) => (
                    <button
                      key={itemIdx}
                      onClick={() => handleNormativeItemSelect(item.query)}
                      className="w-full text-left p-1.5 bg-slate-800/60 hover:bg-emerald-950/80 text-slate-200 hover:text-emerald-200 border border-slate-700/60 hover:border-emerald-400/40 rounded text-[10px] font-mono transition-all flex items-center justify-between group cursor-pointer"
                    >
                      <span className="truncate font-bold">{item.code}</span>
                      <ArrowRight className="w-3 h-3 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-1" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 2. Omni-Input / Intent Bar with Multi-line + File Attachment */}
      <div 
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleProcessFile(file);
        }}
        className={`bg-slate-950/90 border-2 ${isDragging ? 'border-emerald-400 bg-emerald-950/20' : 'border-slate-700 focus-within:border-emerald-500'} rounded-2xl p-3.5 sm:p-4 space-y-3 transition-all shadow-inner relative z-10`}
      >
        <div className="relative">
          <textarea
            rows={3}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                handleDirectExecute();
              }
            }}
            placeholder={
              isDe
                ? "Beschreiben Sie Ihre Aufgabe oder ziehen Sie ein Dokument hierher (z.B. 'Vertragsprüfung auf versteckte Klauseln', 'Aufmaß DIN 276', 'XRechnung prüfen')..."
                : isFr
                ? "Décrivez votre tâche ou déposez un fichier ici (ex. 'Audit de contrat commercial', 'Vérification devis BTP', 'Factur-X')..."
                : isPl
                ? "Opisz zadanie lub upuść dokument tutaj (np. 'Sprawdź umowę najmu pod kątem kar umownych', 'Kosztorys KNR', 'Weryfikacja KSeF FA_VAT')..."
                : isUk 
                ? "Опишіть вашу задачу або перетягніть файл сюди (наприклад: 'Перевірити договір оренди на приховані ризики', 'Скласти кошторис ремонту покрівлі АВК-5', 'Розблокувати податкову накладну')..." 
                : "Describe your business task or drop a document here (e.g. 'Audit commercial lease for hidden penalties', 'Calculate construction estimate', 'Check Delaware charter')..."
            }
            className="w-full bg-transparent text-xs sm:text-sm text-white placeholder-slate-400 focus:outline-none resize-none font-mono leading-relaxed"
          />
        </div>

        {/* Attached File Display Chip */}
        {attachedFile && (
          <div className="flex items-center justify-between p-2.5 bg-emerald-950/60 border border-emerald-500/40 rounded-xl text-xs">
            <div className="flex items-center gap-2.5 min-w-0">
              <FileText className="w-4 h-4 text-emerald-400 shrink-0" />
              <div className="min-w-0">
                <span className="font-bold text-emerald-200 truncate block">{attachedFile.name}</span>
                <span className="text-[10px] text-slate-300 font-mono">
                  {(attachedFile.size / 1024).toFixed(1)} KB • {attachedFile.text.length} {isUk ? 'символів вилучено' : 'chars extracted'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setAttachedFile(null)}
              className="p-1 hover:bg-emerald-900/60 rounded text-slate-400 hover:text-white transition-colors cursor-pointer"
              title={isUk ? 'Видалити файл' : 'Remove file'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* File extraction status */}
        {isExtractingFile && (
          <div className="flex items-center gap-2 text-xs text-blue-300 font-mono bg-blue-950/40 p-2 rounded-lg border border-blue-500/30 animate-pulse">
            <RefreshCw className="w-3.5 h-3.5 animate-spin text-blue-400" />
            <span>{isUk ? 'Вилучення тексту з документа (PDF/Word/Excel/XML)...' : 'Extracting document text...'}</span>
          </div>
        )}
        {fileExtractError && (
          <div className="text-xs text-amber-300 bg-amber-950/40 p-2 rounded-lg border border-amber-500/30 font-mono">
            ⚠️ {fileExtractError}
          </div>
        )}

        {/* Omni-Input Action Toolbar */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-800 flex-wrap gap-2">
          {/* File Upload Trigger */}
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ods,.txt,.csv,.xml,.bdk,.jpg,.jpeg,.png,.webp"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isExtractingFile}
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Paperclip className="w-3.5 h-3.5 text-blue-400" />
              <span>{isUk ? '📎 Прикріпити файл' : isPl ? '📎 Załącz plik' : '📎 Attach document'}</span>
            </button>

            {query && (
              <button
                onClick={handleClear}
                className="px-2.5 py-1.5 text-slate-400 hover:text-white text-xs font-bold transition-colors cursor-pointer"
              >
                {isUk ? 'Очистити' : isPl ? 'Wyczyść' : 'Clear'}
              </button>
            )}
          </div>

          {/* Action Buttons: [ 🔍 Підібрати модулі ] and [ 🚀 Вирішити задачу одразу ] */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => performMatching(query, true)}
              disabled={isAnalyzing || (!query.trim() && !attachedFile)}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white border border-slate-700 rounded-xl text-xs font-extrabold uppercase transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              {isAnalyzing ? (
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Search className="w-3.5 h-3.5 text-blue-400" />
              )}
              <span>{isUk ? '🔍 Підібрати модулі' : isPl ? '🔍 Dobierz moduły' : '🔍 Find Agents'}</span>
            </button>

            <button
              onClick={handleDirectExecute}
              disabled={isAnalyzing || (!query.trim() && !attachedFile)}
              className="px-4 sm:px-5 py-2 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white border border-emerald-400/50 rounded-xl text-xs font-black uppercase transition-all shadow-lg shadow-emerald-600/30 flex items-center gap-2 cursor-pointer disabled:opacity-50 hover:scale-[1.02]"
            >
              {isAnalyzing ? (
                <RefreshCw className="w-4 h-4 animate-spin text-white" />
              ) : (
                <Zap className="w-4 h-4 text-amber-300 animate-pulse" />
              )}
              <span>{isUk ? '🚀 Вирішити задачу одразу' : isPl ? '🚀 Rozwiąż zadanie od razu' : '🚀 Execute Task Directly'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Quick Suggestion Chips */}
      <div className="flex items-center gap-2 flex-wrap z-10 relative">
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mr-1">
          {isDe 
            ? 'Häufige Anfragen:' 
            : isFr 
            ? 'Requêtes fréquentes :' 
            : isIt
            ? 'Richieste frequenti:'
            : isEs
            ? 'Consultas frecuentes:'
            : isPt
            ? 'Consultas frequentes:'
            : isSv
            ? 'Vanliga förfrågningar:'
            : isPl 
            ? 'Częste zapytania:' 
            : isUk 
            ? 'Часті нормативні запити:' 
            : 'Quick prompts:'}
        </span>
        {((ROLE_PROMPTS_MAP[selectedRole] && ROLE_PROMPTS_MAP[selectedRole][language]?.length) 
          ? ROLE_PROMPTS_MAP[selectedRole][language]! 
          : quickPrompts).map((qp, idx) => (
          <button
            key={idx}
            onClick={() => handleQuickPromptClick(qp.query)}
            className="px-2.5 py-1 bg-slate-800/80 hover:bg-emerald-900/50 hover:border-emerald-400/50 text-slate-300 hover:text-emerald-200 border border-slate-700 rounded-lg text-[10px] font-bold transition-all cursor-pointer"
          >
            {qp.label}
          </button>
        ))}
      </div>

      {/* Greeting / Generic Prompt Guidance Banner */}
      {hasSearched && isGreeting && (
        <div className="p-4 sm:p-5 bg-gradient-to-r from-blue-950/90 via-slate-900 to-indigo-950/90 border border-blue-400/40 rounded-xl space-y-2 animate-fade-in z-10 relative shadow-xl">
          <div className="flex items-start gap-3.5">
            <div className="p-2.5 bg-blue-500/20 border border-blue-400/30 rounded-xl text-blue-300 shrink-0">
              <MessageSquare className="w-5 h-5 animate-pulse" />
            </div>
            <div className="space-y-1">
              <h4 className="text-xs sm:text-sm font-extrabold text-blue-200 uppercase tracking-wide flex items-center gap-2">
                <span>
                  {isDe 
                    ? '👋 WILLKOMMEN BEIM INTELLIGENTEN KI-DISPATCHER!'
                    : isFr 
                    ? '👋 BIENVENUE DANS LE DISPATCHER INTELLIGENT IA !'
                    : isIt
                    ? '👋 BENVENUTO NEL DISPATCHER INTELLIGENTE IA!'
                    : isEs
                    ? '👋 ¡BIENVENIDO AL DISPATCHER INTELIGENTE DE IA!'
                    : isPt
                    ? '👋 BEM-VINDO AO DISPATCHER INTELIGENTE IA!'
                    : isSv
                    ? '👋 VÄLKOMMEN TILL DEN INTELLIGENTA AI-DISPATCHERN!'
                    : isPl 
                    ? '👋 WITAJ W INTELIGENTNYM DYSPOZYTORZE AI!' 
                    : isUk 
                    ? '👋 ВІТАЄМО В ІНТЕЛЕКТУАЛЬНОМУ ДИСПЕТЧЕРІ!' 
                    : '👋 WELCOME TO AI DISPATCHER!'}
                </span>
              </h4>
              <p className="text-xs text-slate-200 leading-relaxed font-mono">
                {isDe
                  ? 'Beschreiben Sie Ihre Aufgabe oder wählen Sie eine Rechtsnorm aus dem Menü, um sofort das passende KI-Modul zu starten!'
                  : isFr
                  ? 'Décrivez votre tâche ou choisissez une norme dans le menu déroulant pour trouver immédiatement le module IA adapté !'
                  : isIt
                  ? 'Descrivi la tua specifica richiesta o seleziona uno standard normativo per avviare il modulo dedicato!'
                  : isEs
                  ? '¡Describa su consulta específica o elija una norma del menú desplegable para asignar el módulo de IA relevante!'
                  : isPt
                  ? 'Descreva a sua questão específica ou selecione uma norma no menu para selecionar o módulo de IA adequado!'
                  : isSv
                  ? 'Beskriv din specifika fråga eller välj en standard från menyn så matchar vi rätt AI-modul!'
                  : isPl
                  ? 'Opisz swoje zadanie lub wybierz normę z rozwijanego menu, a my natychmiast dobierzemy odpowiedniego agenta AI!'
                  : isUk 
                  ? 'Будь ласка, опишіть ваше конкретне питання або виберіть норматив із випадаючого підказчика, і ми миттєво підберемо релевантного ШІ-агента!'
                  : 'Please describe your specific task or select a normative code from the drawer!'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scenario 2: Autonomous On-Demand Module Creation & Global Publication Banner */}
      {hasSearched && isMissingAgent && (
        <div className="p-6 bg-gradient-to-r from-slate-900 via-emerald-950/40 to-slate-900 border-2 border-emerald-500/60 rounded-2xl space-y-5 animate-fade-in z-10 relative shadow-2xl">
          {createdPermanentModule ? (
            /* State A: Module Successfully Created and Published */
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-emerald-500/20 border-2 border-emerald-400/60 rounded-xl text-emerald-400 shrink-0">
                  <CheckCircle2 className="w-8 h-8 text-emerald-300 animate-pulse" />
                </div>
                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="text-sm sm:text-base font-black text-emerald-200 uppercase tracking-wide flex items-center gap-2">
                      <span>🎉</span>
                      <span>
                        {isPl
                          ? `MODUŁ «${createdPermanentModule.namePl || createdPermanentModule.nameUk}» ZOSTAŁ OPUBLIKOWANY!`
                          : isUk
                          ? `МОДУЛЬ «${createdPermanentModule.nameUk}» УСПІШНО СТВОРЕНО ТА ОПУБЛІКОВАНО!`
                          : `MODULE "${createdPermanentModule.nameEn || createdPermanentModule.nameUk}" SUCCESSFULLY PUBLISHED!`}
                      </span>
                    </h4>
                    <span className="px-3 py-1 text-[11px] bg-emerald-500/30 text-emerald-200 border border-emerald-400/50 rounded-lg uppercase font-black tracking-wider flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5 text-emerald-400" />
                      <span>9 МОВ (UA / EN / PL / DE / FR / IT / ES / PT / SV)</span>
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-200 leading-relaxed font-sans">
                    {isPl
                      ? `Nowy stały moduł został zarejestrowany w globalnym katalogu platformy i w pełni przetłumaczony na wszystkie 9 języków (UA / EN / PL / DE / FR / IT / ES / PT / SV). Od teraz jest trwale dostępny dla Ciebie oraz wszystkich przedsiębiorstw!`
                      : isUk
                      ? `Новий постійний модуль зареєстровано в загальному каталозі платформи та повністю перекладено всіма 9 мовами (UA / EN / PL / DE / FR / IT / ES / PT / SV). Відтепер він назавжди доступний вам та всім користувачам платформи!`
                      : `The new permanent module is registered in the platform catalog and localized across all 9 platform languages (UA, EN, PL, DE, FR, IT, ES, PT, SV). It is now permanently accessible to you and all enterprises!`}
                  </p>
                  <div className="flex items-center gap-3 pt-2 flex-wrap text-xs font-mono text-emerald-300">
                    <span className="px-2.5 py-1 bg-slate-800/80 rounded-md border border-slate-700">
                      🏛️ {createdPermanentModule.categoryUk}
                    </span>
                    <span className="px-2.5 py-1 bg-slate-800/80 rounded-md border border-slate-700">
                      📋 {isUk ? `Полів форми: ${createdPermanentModule.fields?.length || 3}` : `Input fields: ${createdPermanentModule.fields?.length || 3}`}
                    </span>
                    <span className="px-2.5 py-1 bg-slate-800/80 rounded-md border border-slate-700 text-amber-300">
                      💵 {createdPermanentModule.priceUk}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-4 border-t border-emerald-500/30 flex-wrap">
                <button
                  onClick={() => handleLaunchWithIntent(createdPermanentModule.id)}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs sm:text-sm uppercase rounded-xl transition-all shadow-xl flex items-center gap-2.5 cursor-pointer tracking-wider"
                >
                  <Sparkles className="w-4 h-4 text-amber-300" />
                  <span>
                    {isPl
                      ? '🚀 Otwórz stworzony moduł z Twoimi danymi'
                      : isUk
                      ? '🚀 Відкрити створений модуль із моїми даними'
                      : '🚀 Open Created Module With My Data'}
                  </span>
                </button>

                {onOpenEnterpriseDashboard && (
                  <button
                    onClick={onOpenEnterpriseDashboard}
                    className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white font-bold text-xs uppercase rounded-xl transition-colors border border-slate-700 flex items-center gap-2 cursor-pointer font-mono"
                  >
                    <Building2 className="w-4 h-4 text-blue-400" />
                    <span>
                      {isPl ? '🏢 Panel Firmy (Zadania)' : isUk ? '🏢 Переглянути у Кабінеті Підприємства' : '🏢 Enterprise Dashboard'}
                    </span>
                  </button>
                )}
              </div>
            </div>
          ) : isSynthesizingPermanent ? (
            /* State B: In-Progress Engineering Pipeline Stepper */
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-teal-500/20 border-2 border-teal-400/60 rounded-xl text-teal-300 shrink-0">
                  <RefreshCw className="w-7 h-7 text-teal-300 animate-spin" />
                </div>
                <div className="space-y-2 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="text-xs sm:text-sm font-black text-teal-200 uppercase tracking-wide">
                      {isPl
                        ? '⚙️ AUTONOMICZNY KONWEJER PROJEKTOWANIA MODUŁU (SCENARIUSZ 2)'
                        : isUk
                        ? '⚙️ АВТОНОМНЕ ПРОЄКТУВАННЯ ТА РОЗГОРТАННЯ МОДУЛЯ (СЦЕНАРІЙ 2)'
                        : '⚙️ AUTONOMOUS MODULE ENGINEERING & DEPLOYMENT (SCENARIO 2)'}
                    </h4>
                    <span className="px-2.5 py-0.5 text-[10px] bg-teal-500/30 text-teal-200 border border-teal-400/40 rounded uppercase font-black tracking-wider">
                      {isUk ? '⏳ ~15-20 СЕКУНД' : '⏳ ~15-20 SECONDS'}
                    </span>
                  </div>

                  {/* 4-Step Pipeline Visualizer */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 pt-2">
                    {[
                      { step: 0, titleUk: '1. Нормативна база', titlePl: '1. Baza normatywna', titleEn: '1. Regulatory norms' },
                      { step: 1, titleUk: '2. Структура полів', titlePl: '2. Struktura pól', titleEn: '2. Form fields' },
                      { step: 2, titleUk: '3. Локалізація (9 мов)', titlePl: '3. Lokalizacja (9 języków)', titleEn: '3. All 9 Languages' },
                      { step: 3, titleUk: '4. Публікація в каталог', titlePl: '4. Publikacja w katalogu', titleEn: '4. Catalog publish' }
                    ].map(s => {
                      const isActive = synthesizeStep === s.step;
                      const isDone = synthesizeStep > s.step;
                      return (
                        <div
                          key={s.step}
                          className={`p-2 rounded-lg border text-xs font-mono transition-all flex items-center gap-2 ${
                            isDone
                              ? 'bg-emerald-950/60 border-emerald-500/60 text-emerald-300'
                              : isActive
                              ? 'bg-teal-900/60 border-teal-400 text-teal-200 shadow-md animate-pulse'
                              : 'bg-slate-900/40 border-slate-800 text-slate-500'
                          }`}
                        >
                          {isDone ? (
                            <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          ) : isActive ? (
                            <RefreshCw className="w-3.5 h-3.5 text-teal-300 animate-spin shrink-0" />
                          ) : (
                            <Clock className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                          )}
                          <span className="truncate">
                            {isPl ? s.titlePl : isUk ? s.titleUk : s.titleEn}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-slate-300 font-sans pt-1 leading-relaxed">
                    {isPl
                      ? `Zadanie zostało zarejestrowane. Możesz poczekać na zakończenie lub przejść do innych zadań w Panelu Firmy — moduł zostanie automatycznie opublikowany dla wszystkich.`
                      : isUk
                      ? `Завдання зафіксовано в системі. Ви можете зачекати тут або перейти до інших задач у Кабінеті Підприємства — модуль створюється у фоні та буде доступний усім!`
                      : `Task enqueued. You can wait or switch to Enterprise Dashboard — module is being synthesized and published in the background!`}
                  </p>
                </div>
              </div>

              {onOpenEnterpriseDashboard && (
                <div className="pt-3 border-t border-teal-500/30 flex justify-end">
                  <button
                    onClick={onOpenEnterpriseDashboard}
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-mono text-xs rounded-xl transition-colors border border-slate-700 flex items-center gap-2 cursor-pointer"
                  >
                    <Building2 className="w-3.5 h-3.5 text-blue-400" />
                    <span>
                      {isPl ? '🏢 Przejdź do Panelu Firmy (zajrzyj później)' : isUk ? '🏢 Перейти в Кабінет Підприємства (зайти пізніше)' : '🏢 Open Enterprise Dashboard (return later)'}
                    </span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* State C: Call-To-Action to create Scenario 2 Permanent Module */
            <div className="space-y-4">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 bg-amber-500/20 border border-amber-500/40 rounded-xl text-amber-400 shrink-0">
                  <Sparkles className="w-6 h-6 animate-pulse text-amber-300" />
                </div>
                <div className="space-y-1.5 flex-1">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <h4 className="text-xs sm:text-sm font-black text-amber-200 uppercase tracking-wide">
                      {isPl 
                        ? '⚡ STWÓRZ I OPUBLIKUJ STAŁY MODUŁ DLA WSZYSTKICH (SCENARIUSZ 2)' 
                        : isUk 
                        ? '⚡ СТВОРИТИ ТА ОПУБЛІКУВАТИ ПОСТІЙНИЙ МОДУЛЬ НА ПЛАТФОРМІ (СЦЕНАРІЙ 2)' 
                        : '⚡ BUILD & PUBLISH PERMANENT MODULE FOR ALL USERS (SCENARIO 2)'}
                    </h4>
                    <span className="px-2.5 py-0.5 text-[10px] bg-emerald-500/30 text-emerald-300 border border-emerald-400/40 rounded uppercase font-extrabold tracking-wider">
                      {isUk ? '🌐 ДОСТУПНИЙ ДЛЯ ВСІХ НА 9 МОВАХ' : isPl ? '🌐 DLA WSZYSTKICH W 9 JĘZYKACH' : '🌐 FOR ALL USERS IN 9 LANGUAGES'}
                    </span>
                  </div>
                  <p className="text-xs text-amber-100/90 leading-relaxed font-sans">
                    {isPl
                      ? `Dla zadania «${query}» nie ma jeszcze stałego modułu w katalogu. Nasz system może automatycznie stworzyć ten moduł, przetłumaczyć na wszystkie 9 języków platformy (UA/EN/PL/DE/FR/IT/ES/PT/SV) i opublikować w globalnym katalogu dla Ciebie i innych użytkowników!`
                      : isUk 
                      ? `Під вашу задачу «${query}» ще немає окремого модуля в каталозі. Інженерна система створить повноцінний професійний модуль під ваші вимоги, локалізує на всі 9 мов платформи (UA / EN / PL / DE / FR / IT / ES / PT / SV) та назавжди додасть до каталогу платформи для вас та інших підприємств!`
                      : `There is no pre-built static module for "${query}" yet. The system can synthesize a dedicated module, localize it across all 9 platform languages (UA/EN/PL/DE/FR/IT/ES/PT/SV), and publish it permanently in the platform catalog for you and all enterprises!`}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-amber-500/30 flex-wrap">
                <div className="flex items-center gap-2.5 flex-wrap">
                  {/* Primary Scenario 2 Action */}
                  <button
                    onClick={handleCreatePermanentModule}
                    disabled={isSynthesizingPermanent}
                    className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs sm:text-sm uppercase rounded-xl transition-all shadow-xl flex items-center gap-2 cursor-pointer disabled:opacity-50 tracking-wider"
                  >
                    <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                    <span>
                      {isPl 
                        ? '🚀 Stwórz i opublikuj stały moduł (15-20 sek)' 
                        : isUk 
                        ? '🚀 Створити та опублікувати модуль (15-20 сек)' 
                        : '🚀 Build & Publish Permanent Module (15-20s)'}
                    </span>
                  </button>

                  {/* Quick Dynamic Mode Alternative */}
                  <button
                    onClick={handleGenerateDynamicAgent}
                    disabled={isGeneratingDynamicAgent}
                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-bold text-xs uppercase rounded-xl transition-colors border border-slate-700 flex items-center gap-1.5 cursor-pointer disabled:opacity-50 font-mono"
                  >
                    <Zap className="w-3.5 h-3.5 text-amber-400" />
                    <span>
                      {isUk ? '⚡ Відкрити на льоту' : isPl ? '⚡ Na bieżąco' : '⚡ On-the-fly'}
                    </span>
                  </button>
                </div>

                {/* Secondary Action: Send Request to Telegram */}
                <div className="flex items-center gap-2 text-xs font-bold text-slate-300">
                  {requestSent ? (
                    <div className="flex items-center gap-1.5 text-emerald-400 font-mono text-xs">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span>{isUk ? '✓ Запит розробникам надіслано' : '✓ Request sent to developers'}</span>
                    </div>
                  ) : (
                    <button
                      onClick={handleManualSendTelegramRequest}
                      disabled={isSendingRequest}
                      className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white font-extrabold text-xs uppercase rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer border border-slate-700 disabled:opacity-50"
                    >
                      <Send className="w-3.5 h-3.5 text-blue-400" />
                      <span>
                        {isSendingRequest 
                          ? (isUk ? 'Надсилання...' : 'Sending...') 
                          : (isUk ? '📩 Надіслати розробникам' : '📩 Notify developers')}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Recommendations Output Results */}
      {hasSearched && recommendations.length > 0 && (
        <div className="space-y-3 pt-3 border-t border-slate-800 z-10 relative animate-fade-in">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-extrabold text-emerald-300 uppercase tracking-wider flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span>
                {isDe
                  ? `EMPFOHLENE KI-MODULE (${recommendations.length}):`
                  : isFr
                  ? `MODULES IA RECOMMANDÉS (${recommendations.length}):`
                  : isIt
                  ? `MODULI CONSIGLIATI (${recommendations.length}):`
                  : isEs
                  ? `MÓDULOS RECOMENDADOS (${recommendations.length}):`
                  : isPt
                  ? `MÓDULOS RECOMENDADOS (${recommendations.length}):`
                  : isSv
                  ? `REKOMMENDERADE MODULER (${recommendations.length}):`
                  : isPl 
                  ? `REKOMENDOWANE NARZĘDZIA (${recommendations.length}):` 
                  : isUk 
                  ? `РЕКОМЕНДОВАНІ НОРМАТИВНІ ІНСТРУМЕНТИ (${recommendations.length}):` 
                  : `RECOMMENDED AGENTS (${recommendations.length}):`}
              </span>
            </h3>
            <span className="text-[10px] text-slate-400 font-mono">
              {isDe 
                ? '100% Konformität mit deutschen & EU-Normen'
                : isFr 
                ? '100% de conformité légale et réglementaire'
                : isIt
                ? '100% di conformità a normative italiane ed europee'
                : isEs
                ? '100% de cumplimiento con normativas españolas y europeas'
                : isPt
                ? '100% de conformidade com normas portuguesas e europeias'
                : isSv
                ? '100% efterlevnad av svenska lagar och EU-standarder'
                : isPl 
                ? '100% Zgodności z przepisami i normami' 
                : isUk 
                ? '100% Комплаєнс з законодавством України' 
                : '100% compliant with regulations'}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recommendations.map((rec) => (
              <div
                key={rec.id}
                className="bg-slate-950/90 border border-emerald-500/30 hover:border-emerald-400 rounded-xl p-4 flex flex-col justify-between gap-3 transition-all hover:scale-[1.02] shadow-lg group"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded">
                      {isDe 
                        ? (rec.categoryDe || rec.categoryEn || rec.categoryUk)
                        : isFr
                        ? (rec.categoryFr || rec.categoryEn || rec.categoryUk)
                        : isIt
                        ? (rec.categoryIt || rec.categoryEn || rec.categoryUk)
                        : isEs
                        ? (rec.categoryEs || rec.categoryEn || rec.categoryUk)
                        : isPt
                        ? (rec.categoryPt || rec.categoryEn || rec.categoryUk)
                        : isSv
                        ? (rec.categorySv || rec.categoryEn || rec.categoryUk)
                        : isPl 
                        ? (rec.categoryPl || rec.categoryEn || rec.categoryUk) 
                        : isUk 
                        ? rec.categoryUk 
                        : (rec.categoryEn || rec.categoryUk)}
                    </span>
                    <span className="text-[9px] text-emerald-400 font-bold bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-500/30">
                      ✓ {isDe ? (rec.matchReasonDe || rec.matchReasonEn) : isFr ? (rec.matchReasonFr || rec.matchReasonEn) : isIt ? (rec.matchReasonIt || rec.matchReasonEn) : isEs ? (rec.matchReasonEs || rec.matchReasonEn) : isPt ? (rec.matchReasonPt || rec.matchReasonEn) : isSv ? (rec.matchReasonSv || rec.matchReasonEn) : isPl ? rec.matchReasonPl : isUk ? rec.matchReasonUk : rec.matchReasonEn}
                    </span>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-900/30 border border-emerald-500/30 rounded-lg text-emerald-400 group-hover:text-emerald-300 shrink-0">
                      <AgentIcon name={rec.icon} className="w-5 h-5" />
                    </div>
                    <h4 className="text-xs font-extrabold text-white uppercase group-hover:text-emerald-300 transition-colors leading-tight">
                      {isDe 
                        ? (rec.nameDe || rec.nameEn || rec.nameUk)
                        : isFr
                        ? (rec.nameFr || rec.nameEn || rec.nameUk)
                        : isIt
                        ? (rec.nameIt || rec.nameEn || rec.nameUk)
                        : isEs
                        ? (rec.nameEs || rec.nameEn || rec.nameUk)
                        : isPt
                        ? (rec.namePt || rec.nameEn || rec.nameUk)
                        : isSv
                        ? (rec.nameSv || rec.nameEn || rec.nameUk)
                        : isPl 
                        ? (rec.namePl || rec.nameEn || rec.nameUk) 
                        : isUk 
                        ? rec.nameUk 
                        : (rec.nameEn || rec.nameUk)}
                    </h4>
                  </div>

                  <p className="text-[11px] text-slate-400 font-mono line-clamp-2 leading-relaxed">
                    {isDe 
                      ? (rec.descriptionDe || rec.descriptionEn || rec.descriptionUk)
                      : isFr
                      ? (rec.descriptionFr || rec.descriptionEn || rec.descriptionUk)
                      : isIt
                      ? (rec.descriptionIt || rec.descriptionEn || rec.descriptionUk)
                      : isEs
                      ? (rec.descriptionEs || rec.descriptionEn || rec.descriptionUk)
                      : isPt
                      ? (rec.descriptionPt || rec.descriptionEn || rec.descriptionUk)
                      : isSv
                      ? (rec.descriptionSv || rec.descriptionEn || rec.descriptionUk)
                      : isPl 
                      ? (rec.descriptionPl || rec.descriptionEn || rec.descriptionUk) 
                      : isUk 
                      ? rec.descriptionUk 
                      : (rec.descriptionEn || rec.descriptionUk)}
                  </p>
                </div>

                <button
                  onClick={() => handleLaunchWithIntent(rec.id)}
                  className="w-full py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-extrabold text-xs uppercase rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer shadow-md"
                >
                  <span>
                    {isDe
                      ? '🚀 Zur Aufgabenausführung'
                      : isFr
                      ? '🚀 Ouvrir le Module Dédié'
                      : isIt
                      ? '🚀 Apri il Modulo Dedicato'
                      : isEs
                      ? '🚀 Abrir Módulo Especializado'
                      : isPt
                      ? '🚀 Abrir Módulo Especializado'
                      : isSv
                      ? '🚀 Öppna Specialmodul'
                      : isPl 
                      ? '🚀 Przejdź do Realizacji Zadania' 
                      : isUk 
                      ? '🚀 Перейти до Виконання Задачі' 
                      : '🚀 Open Solution'}
                  </span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
